/**
 * Field overlay map for Форма 001/о (PDF form).
 *
 * Coordinates are percentages of page width/height, measured from
 * the top-left corner. Calibrated against actual PDF text positions
 * extracted via PyMuPDF (page size 595.276 × 841.890 pts).
 *
 * Checkbox size: the PDF squares are roughly 6×8 pts = ~1.0% × 0.95%.
 * We place each checkbox so its centre aligns with the printed square.
 */

import { MILITARY_RANK_VALUES } from "../../../../shared/src/enums";

export interface FieldOverlay {
  id: string;
  /** Human-readable label shown in the edit popover title */
  label?: string;
  page: 1 | 2;
  x: number;
  y: number; // % from top-left
  w: number;
  h: number; // % of page
  type:
    | "text"
    | "number"
    | "select"
    | "checkbox"
    | "time"
    | "date"
    | "textarea"
    | "patient-name"
    | "datetime-date"
    | "blood-rh"
    | "vision"
    | "signature"
    | "unit-autocomplete"
    | "facility-autocomplete"
    | "hospital-select";
  isPii?: boolean;
  getValue: (data: any) => any;
  command: string;
  getPayload: (
    value: any,
    data: any,
    newUuid?: string,
  ) => Record<string, unknown> | null;
  options?: { value: string; label: string }[];
  /** If set, PdfForm auto-creates a row before dispatching when getPayload returns null */
  autoCreate?: {
    command: string;
    getPayload: (caseId: string) => Record<string, unknown>;
  };
  /** Extra width (%) beyond w for clickable hit zone — extends over the printed label */
  labelW?: number;
  /** Full label hit rectangle {x,y,w,h} in page-% — overrides labelW when present */
  labelRect?: { x: number; y: number; w: number; h: number };
  /** First-line text indent in % of page width (for textarea fields) */
  textIndent?: number;
  /** Last-line right cutoff in % of page width (for textarea fields — inverse of textIndent) */
  lastLineCutoff?: number;
  /** Line height in % of page height (for textarea fields — aligns text to printed underlines) */
  lineHeight?: number;
  /** Group ID for radio-like exclusive checkboxes — siblings in the same group deselect optimistically */
  radioGroup?: string;
  /** Human-readable label for this option inside a radioGroup (shown in the popover) */
  radioLabel?: string;
  /** The raw value string this radio option represents */
  radioValue?: string;
  /** Override edit popover type — e.g. 'text' for a textarea that edits as single-line */
  editAs?: "text" | "textarea";
  /** Return minimum allowed value (for time fields — times before this are disabled) */
  minValue?: (data: any) => any;
}

// ─── Helpers ──────────────────────────────────────────────────────────

const cid = (d: any) => d.case.id;
const pid = (d: any) => d.patient?.id;

// Checkbox dimensions — match the small printed squares in the PDF
const CW = 0.8685; // checkbox width % (5.17pt / 595.28)
const CH = 0.6141; // checkbox height % (5.17pt / 841.89)

// Reusable select options
const BLOOD_TYPE_OPTIONS = [
  { value: "A", label: "A" },
  { value: "B", label: "B" },
  { value: "AB", label: "AB" },
  { value: "O", label: "O" },
  { value: "unknown", label: "?" },
];
const RH_FACTOR_OPTIONS = [
  { value: "positive", label: "+" },
  { value: "negative", label: "-" },
  { value: "unknown", label: "?" },
];

/**
 * Create individually-placed checkboxes for a radio-like exclusive group.
 * Each option is a separate checkbox positioned at its exact SVG rect location.
 * Clicking an already-selected option deselects it (sets value to null).
 *
 * @param idPrefix  e.g. 'p1-triage'
 * @param page      1 | 2
 * @param command   e.g. 'case.setTriageCategory'
 * @param getValue  reads the current value from data, e.g. d => d.case.triageCategory
 * @param mkPayload builds the command payload given the new value
 * @param opts      array of { value, x, y, labelW? } — x,y are the SVG rect %
 */
function radioOpts(
  idPrefix: string,
  page: 1 | 2,
  command: string,
  getValue: (d: any) => any,
  mkPayload: (
    val: string | null,
    d: any,
    newUuid?: string,
  ) => Record<string, unknown> | null,
  opts: Array<{
    value: string;
    label?: string;
    x: number;
    y: number;
    labelW?: number;
    labelRect?: { x: number; y: number; w: number; h: number };
  }>,
  autoCreate?: FieldOverlay["autoCreate"],
): FieldOverlay[] {
  return opts.map((o) => {
    const lw = o.labelW ?? 5;
    const rl = o.label ?? o.value;
    return {
      id: `${idPrefix}-${o.value}`,
      label: rl,
      page,
      x: o.x,
      y: o.y,
      w: CW,
      h: CH,
      type: "checkbox" as const,
      labelW: lw,
      labelRect: o.labelRect ?? { x: o.x, y: o.y, w: CW + lw, h: CH },
      radioGroup: idPrefix,
      radioLabel: rl,
      radioValue: o.value,
      getValue: (d: any) => {
        const v = getValue(d);
        return v != null && String(v) === o.value;
      },
      command,
      getPayload: (checked: any, d: any, newUuid?: string) =>
        mkPayload(checked ? o.value : null, d, newUuid),
      autoCreate,
    };
  });
}


// ─── Page 1 field coordinates (calibrated from PDF) ──────────────────
// PDF page: 595.276 × 841.890 pts

export const FORM_FIELDS: FieldOverlay[] = [
  // ═══ Section 1: General Information ═══
  {
    id: "p1-case-number",
    label: "№",
    page: 1,
    x: 89.77,
    y: 3.144,
    w: 6.803,
    h: 1.2,
    type: "text",
    getValue: (d) => d.case.number,
    command: "case.setNumber",
    getPayload: (v, d) => ({ uuid: cid(d), number: v }),
  },

  {
    id: "p1-name",
    label: "ПІБ",
    page: 1,
    x: 6.23,
    y: 8.44,
    w: 21.6,
    h: 2.57,
    lineHeight: 1.25,
    labelW: -1,
    type: "patient-name",
    getValue: (d) => d.patient?.name,
    command: "patient.setName",
    getPayload: (v, d) => (pid(d) ? { uuid: pid(d), name: v } : null),
  },
  ...radioOpts(
    "p1-sex",
    1,
    "patient.setSex",
    (d) => d.patient?.sex,
    (v, d) => (pid(d) ? { uuid: pid(d), sex: v } : null),
    [
      { value: "unknown", label: "Невідомо", x: 3.817, y: 9.336, labelW: 7.08 },
      { value: "male", label: "Ч", x: 13.34, y: 9.336, labelW: 0.91 },
      { value: "female", label: "Ж", x: 15.76, y: 9.336, labelW: 1.24 },
    ],
  ),
  ...radioOpts(
    "p1-patient-category",
    1,
    "patient.setCategory",
    (d) => d.patient?.patientCategory,
    (v, d) => (pid(d) ? { uuid: pid(d), category: v } : null),
    [
      { value: "civilian", label: "Ц", x: 19.36, y: 9.336, labelW: 1.13 },
      { value: "military", label: "В", x: 22.12, y: 9.336, labelW: 1.01 },
      { value: "police", label: "П", x: 24.72, y: 9.336, labelW: 1.13 },
    ],
  ),
  {
    id: "p1-id",
    isPii: true,
    label: "ID номер",
    page: 1,
    x: 5.468,
    y: 11.21,
    w: 9.402,
    h: 1.2,
    type: "text",
    getValue: (d) => d.patient?.idNumber,
    command: "patient.setIdNumber",
    getPayload: (v, d) => (pid(d) ? { uuid: pid(d), idNumber: v } : null),
  },
  {
    id: "p1-dob",
    isPii: true,
    label: "Дата народження",
    page: 1,
    x: 18.53,
    y: 11.21,
    w: 9.248,
    h: 1.2,
    type: "date",
    getValue: (d) => d.patient?.dateOfBirth,
    command: "patient.setDateOfBirth",
    getPayload: (v, d) => (pid(d) ? { uuid: pid(d), date: v } : null),
  },

  {
    id: "p1-rank",
    isPii: true,
    label: "В/звання",
    page: 1,
    x: 35.16,
    y: 7.306,
    w: 12.84,
    h: 1.2,
    type: "select",
    getValue: (d) => d.patient?.militaryRank,
    command: "patient.setMilitaryRank",
    getPayload: (v, d) => (pid(d) ? { uuid: pid(d), rank: v } : null),
    options: MILITARY_RANK_VALUES.map((v) => ({ value: v, label: v })),
  },
  {
    id: "p1-unit",
    label: "В/частина",
    page: 1,
    x: 36.11,
    y: 8.585,
    w: 11.89,
    h: 1.2,
    type: "unit-autocomplete",
    getValue: (d) => {
      const uid = d.patient?.unitId;
      return { unitId: uid, unitName: d.units?.find((u: any) => u.id === uid)?.name || "" };
    },
    command: "patient.setUnit",
    getPayload: (v, d) => (pid(d) ? { uuid: pid(d), unitUuid: v } : null),
  },
  // Алергія — Ні checkbox at (35.8%, 9.34%), Невідомо at (39.2%, 9.34%), Так at (29.2%, 10.59%)
  ...radioOpts(
    "p1-allergy-status",
    1,
    "patient.setAllergyStatus",
    (d) => d.patient?.allergyStatus,
    (v, d) => (pid(d) ? { uuid: pid(d), status: v } : null),
    [
      { value: "no", label: "Ні", x: 35.77, y: 9.336, labelW: 1.46 },
      { value: "unknown", label: "Невідомо", x: 39.16, y: 9.336, labelW: 6.1 },
      { value: "yes", label: "Так", x: 29.16, y: 10.59, labelW: 2.22 },
    ],
  ),
  {
    id: "p1-allergy-details",
    label: "Алергія",
    page: 1,
    x: 32.63,
    y: 11.21,
    w: 15.37,
    h: 1.2,
    type: "text",
    getValue: (d) => d.patient?.allergyDetails,
    command: "patient.setAllergyDetails",
    getPayload: (v, d) => (pid(d) ? { uuid: pid(d), details: v } : null),
  },

  {
    id: "p1-event-time",
    label: "Подія Час",
    page: 1,
    x: 61.68,
    y: 7.306,
    w: 5.947,
    h: 1.2,
    type: "time",
    getValue: (d) => d.case.eventTime,
    command: "case.setEventTime",
    getPayload: (v, d) => ({ uuid: cid(d), timestamp: v }),
  },
  {
    id: "p1-event-date",
    label: "Подія Дата",
    page: 1,
    x: 73.67,
    y: 7.306,
    w: 10.61,
    h: 1.2,
    type: "datetime-date",
    getValue: (d) => d.case.eventTime,
    command: "case.setEventTime",
    getPayload: (v, d) => ({ uuid: cid(d), timestamp: v }),
  },
  {
    id: "p1-arrival-time",
    label: "Прибуття Час",
    page: 1,
    x: 61.68,
    y: 8.585,
    w: 5.947,
    h: 1.2,
    type: "time",
    getValue: (d) => d.case.arrivalTime,
    command: "case.setArrivalTime",
    getPayload: (v, d) => ({ uuid: cid(d), timestamp: v }),
    minValue: (d) => d.case.eventTime,
  },
  {
    id: "p1-arrival-date",
    label: "Прибуття Дата",
    page: 1,
    x: 73.67,
    y: 8.585,
    w: 10.61,
    h: 1.2,
    type: "datetime-date",
    getValue: (d) => d.case.arrivalTime,
    command: "case.setArrivalTime",
    getPayload: (v, d) => ({ uuid: cid(d), timestamp: v }),
    minValue: (d) => d.case.eventTime,
  },
  arrayToggleCb("p1-transport-casevac", 1, 49.64, 9.336, "casevac", "case.setTransportTypeItem", "", (c) => c.transportType, 5.1, "CASEVAC", { field: "arrival" }),
  arrayToggleCb("p1-transport-mmpm", 1, 56.28, 9.336, "mmpm", "case.setTransportTypeItem", "", (c) => c.transportType, 4.6, "ММПМ", { field: "arrival" }),
  arrayToggleCb("p1-transport-medevac", 1, 62.58, 9.336, "medevac", "case.setTransportTypeItem", "", (c) => c.transportType, 5.63, "MEDEVAC", { field: "arrival" }),
  arrayToggleCb("p1-transport-other", 1, 70.19, 9.336, "other", "case.setTransportTypeItem", "", (c) => c.transportType, 5.61, "Інше", { field: "arrival" }),
  ...radioOpts(
    "p1-mp-role",
    1,
    "case.setRole",
    (d) => d.case.role,
    (v, d) => ({ uuid: cid(d), role: v }),
    [
      { value: "1", x: 77.03, y: 9.336, labelW: 0.75 },
      { value: "2", x: 79.25, y: 9.336, labelW: 0.88 },
      { value: "3", x: 81.72, y: 9.336, labelW: 0.89 },
      { value: "4", x: 84.12, y: 9.336, labelW: 0.92 },
    ],
  ),
  {
    id: "p1-facility",
    label: "Медичний підрозділ",
    page: 1,
    x: 65.82,
    y: 11.21,
    w: 20.21,
    h: 1.2,
    type: "text",
    getValue: (d) => d.case.medicalFacility,
    command: "case.setMedicalFacility",
    getPayload: (v, d) => ({ uuid: cid(d), facility: v }),
  },

  // 1.4 Сортувальна категорія — rects I(87.48%,9.34%) II(90.17%,9.34%) III(87.48%,10.59%) IV(91.07%,10.59%)
  ...radioOpts(
    "p1-triage",
    1,
    "case.setTriageCategory",
    (d) => d.case.triageCategory,
    (v, d) => ({ uuid: cid(d), triageCategory: v }),
    [
      { value: "I", x: 87.48, y: 9.336, labelW: 0.78 },
      { value: "II", x: 90.17, y: 9.336, labelW: 1.18 },
      { value: "III", x: 87.48, y: 10.59, labelW: 1.6 },
      { value: "IV", x: 91.07, y: 10.59, labelW: 1.72 },
    ],
  ),

  // ═══ Section 2: Prior care ═══
  {
    id: "p1-prior-care-time",
    label: "Час",
    page: 1,
    x: 6.308,
    y: 15.01,
    w: 5.947,
    h: 1.2,
    type: "time",
    getValue: (d) => d.case.priorCareTime,
    command: "case.setPriorCareTime",
    getPayload: (v, d) => ({ uuid: cid(d), timestamp: v }),
  },
  {
    id: "p1-prior-care-date",
    label: "Дата",
    page: 1,
    x: 16.95,
    y: 15.01,
    w: 10.61,
    h: 1.2,
    type: "datetime-date",
    getValue: (d) => d.case.priorCareTime,
    command: "case.setPriorCareTime",
    getPayload: (v, d) => ({ uuid: cid(d), timestamp: v }),
  },
  ...makePriorCareFields(),

  // ═══ Section 3: Trauma/Illness ═══
  ...makeTraumaFields(),

  // ═══ Section 4: R1 Treatments ═══
  ...makeR1CareFields(),

  // ═══ Section 4.1: Tourniquets (Page 1) ═══
  // Column headers: Накл.(10.7%) Зняття(15%) Конв.(20.2%) Перем.(24.4%)
  ...makeTourniquetRows(1, [
    { limb: "rue" as const, y: 43.83 },
    { limb: "lue" as const, y: 45.07 },
    { limb: "rle" as const, y: 46.29 },
    { limb: "lle" as const, y: 47.6 },
  ]),

  // ═══ Section 5: Vitals (Page 1) ═══
  ...makeVitalsRows(1, [57.2034, 58.6003, 59.9366], [0, 1, 2]),

  // ═══ Section 6: Ventilation (Page 1) ═══
  ...makeVentilationRows(1, [57.2034, 58.6003, 59.9366], [0, 1, 2]),

  // ═══ Section 7: CPR (Page 1) ═══
  {
    id: "p1-cpr-start",
    label: "СЛР Початок",
    page: 1,
    x: 17.62,
    y: 61.84,
    w: 6.326,
    h: 1.2,
    type: "time",
    getValue: (d) => {
      const row = (d.cpr || []).find((c: any) => (c.role ?? 1) === 1);
      return row?.startTime;
    },
    command: "cpr.setStartTime",
    getPayload: (v, d, newUuid) => {
      const row = (d.cpr || []).find((c: any) => (c.role ?? 1) === 1);
      const uuid = newUuid || row?.id;
      return uuid ? { uuid, timestamp: v } : null;
    },
    autoCreate: {
      command: "cpr.create",
      getPayload: (caseId) => ({ caseUuid: caseId, role: 1 }),
    },
  },
  {
    id: "p1-cpr-end",
    label: "СЛР Кінець",
    page: 1,
    x: 17.62,
    y: 63.18,
    w: 6.326,
    h: 1.2,
    type: "time",
    getValue: (d) => {
      const row = (d.cpr || []).find((c: any) => (c.role ?? 1) === 1);
      return row?.endTime;
    },
    command: "cpr.setEndTime",
    getPayload: (v, d, newUuid) => {
      const row = (d.cpr || []).find((c: any) => (c.role ?? 1) === 1);
      const uuid = newUuid || row?.id;
      return uuid ? { uuid, timestamp: v } : null;
    },
    autoCreate: {
      command: "cpr.create",
      getPayload: (caseId) => ({ caseUuid: caseId, role: 1 }),
    },
    minValue: (d) => {
      const row = (d.cpr || []).find((c: any) => (c.role ?? 1) === 1);
      return row?.startTime;
    },
  },

  // ═══ Section 8: Death (Page 1) ═══
  {
    id: "p1-death-circumstances",
    label: "Обставини",
    page: 1,
    x: 25.49,
    y: 62.72,
    w: 70.66,
    h: 2.523,
    type: "textarea",
    textIndent: 42.2,
    lineHeight: 1.29,
    labelW: 0,
    getValue: (d) => d.case.deathCircumstances,
    command: "case.setDeathCircumstances",
    getPayload: (v, d) => ({ uuid: cid(d), circumstances: v }),
  },
  {
    id: "p1-death-time",
    label: "Смерть Час",
    page: 1,
    x: 38.02,
    y: 61.71,
    w: 6.326,
    h: 1.2,
    type: "time",
    labelRect: { x: 35.55, y: 60.71, w: 2.5, h: 1.2 },
    getValue: (d) => d.case.deathTime,
    command: "case.setDeathTime",
    getPayload: (v, d) => ({ uuid: cid(d), timestamp: v }),
  },
  {
    id: "p1-death-date",
    label: "Смерть Дата",
    page: 1,
    x: 48.5,
    y: 61.71,
    w: 10.61,
    h: 1.2,
    type: "datetime-date",
    labelRect: { x: 45.22, y: 60.71, w: 3.395, h: 1.2 },
    getValue: (d) => d.case.deathTime,
    command: "case.setDeathTime",
    getPayload: (v, d) => ({ uuid: cid(d), timestamp: v }),
  },

  // ═══ Section 9: Diagnostics (Page 1) ═══
  ...makeDiagnosticsFields(1),

  // ═══ Section 10: Medications (Page 1) ═══
  // Left: Назва(3.6%) Доза/Об'єм+Шлях(28.4%) Час(45.2%)
  // Right: Назва(50.6%) Доза/Об'єм+Шлях(75.2%) Час(92.1%)
  ...makeMedicationRows(
    1,
    [72.1116, 73.3742, 74.6368, 75.8995, 77.2654],
    [0, 1, 2, 3, 4],
    "left",
  ),
  ...makeMedicationRows(
    1,
    [72.1116, 73.3742, 74.6368, 75.8995, 77.2654],
    [0, 1, 2, 3, 4],
    "right",
  ),

  // ═══ Blood type ═══
  {
    id: "p1-blood-type",
    label: "Група крові",
    page: 1,
    x: 61.62,
    y: 79.17,
    w: 5.713,
    h: 1.2,
    type: "blood-rh",
    getValue: (d) => d.patient?.bloodType,
    command: "patient.setBloodType",
    getPayload: (v, d) => (pid(d) ? { uuid: pid(d), bloodType: v } : null),
    options: BLOOD_TYPE_OPTIONS,
  },
  {
    id: "p1-rh-factor",
    label: "Rh",
    page: 1,
    x: 69.92,
    y: 79.17,
    w: 5.715,
    h: 1.2,
    type: "blood-rh",
    getValue: (d) => d.patient?.rhFactor,
    command: "patient.setRhFactor",
    getPayload: (v, d) => (pid(d) ? { uuid: pid(d), rhFactor: v } : null),
    options: RH_FACTOR_OPTIONS,
  },

  // "Не визначалась" blood type checkbox (P1)
  {
    id: "p1-blood-unknown",
    label: "Не визначалась",
    page: 1,
    x: 77.61,
    y: 78.4,
    w: CW,
    h: CH,
    type: "checkbox",
    labelW: 9.83,
    getValue: (d) => d.patient?.bloodType === "unknown",
    command: "patient.setBloodType",
    getPayload: (v, d) =>
      pid(d) ? { uuid: pid(d), bloodType: v ? "unknown" : "" } : null,
  },

  // ═══ Section boundary checkboxes (between medications and blood/tetanus) ═══
  // Tetanus administered checkbox — left side
  {
    id: "p1-r1care-tetanusAdministered",
    label: "Профілактика правця",
    page: 1 as const,
    x: 3.864,
    y: 78.4,
    w: CW,
    h: CH,
    type: "checkbox" as const,
    getValue: (d: any) => d.case.r1Care?.tetanusAdministered,
    command: "case.setR1CareItem",
    getPayload: (v: any, d: any) => ({
      uuid: cid(d),
      key: "tetanusAdministered",
      value: !!v,
    }),
  },
  // Blood transfused checkbox — right side
  cb(
    "p1-r1care-bloodTransfused",
    1,
    50.6266,
    78.3974,
    "case.setR1CareItem",
    (d: any) => d.case.r1Care?.bloodTransfused,
    (v: any, d: any) => ({ uuid: cid(d), key: "bloodTransfused", value: !!v }),
    9.64,
    "Гемотрансфузія",
  ),

  // ═══ Tetanus ═══
  {
    id: "p1-tetanus",
    label: "Профілактика правця",
    page: 1,
    x: 18.28,
    y: 79.17,
    w: 30.48,
    h: 1.2,
    type: "text",
    getValue: (d) => d.case.tetanusProphylaxis,
    command: "case.setTetanusProphylaxis",
    getPayload: (v, d) => ({ uuid: cid(d), prophylaxis: v }),
  },

  // ═══ Section 11: Notes ═══
  {
    id: "p1-notes",
    label: "Нотатки",
    page: 1,
    x: 3.8,
    y: 83.95,
    w: 92.42,
    h: 5.08,
    type: "textarea",
    textIndent: 9.75,
    lastLineCutoff: 17,
    lineHeight: 1.25,
    getValue: (d) => d.case.notes,
    command: "case.setNotes",
    getPayload: (v, d) => ({ uuid: cid(d), notes: v }),
  },

  // ═══ Section 12: Diagnosis ═══
  {
    id: "p1-diagnosis",
    label: "Діагноз",
    page: 1,
    x: 3.61,
    y: 89.91,
    w: 92.59,
    h: 6.07,
    type: "textarea",
    textIndent: 10,
    lineHeight: 1.2,
    getValue: (d) => d.case.diagnosis,
    command: "case.setDiagnosis",
    getPayload: (v, d) => ({ uuid: cid(d), diagnosis: v }),
  },

  // ═══ Kit checkboxes (rendered after textareas so they're on top) ═══
  {
    id: "p1-kit1",
    label: "Комплект 1",
    page: 1,
    x: 79.04,
    y: 84,
    w: CW,
    h: CH,
    type: "checkbox",
    labelW: 6.92,
    getValue: (d) => !!d.case.r1Kit1,
    command: "case.setR1Kit1",
    getPayload: (v, d) => ({ uuid: cid(d), kit: v ? "1" : "" }),
  },
  {
    id: "p1-kit2",
    label: "Комплект 2",
    page: 1,
    x: 88.21,
    y: 84,
    w: CW,
    h: CH,
    type: "checkbox",
    labelW: 7.08,
    getValue: (d) => !!d.case.r1Kit2,
    command: "case.setR1Kit2",
    getPayload: (v, d) => ({ uuid: cid(d), kit: v ? "1" : "" }),
  },

  // ═══ Section 13: Discharge ═══
  {
    id: "p1-discharge-time",
    label: "Час",
    page: 1,
    x: 16.76,
    y: 92.66,
    w: 5.947,
    h: 1.2,
    type: "time",
    getValue: (d) => d.case.dischargeTime,
    command: "case.setDischargeTime",
    getPayload: (v, d) => ({ uuid: cid(d), timestamp: v }),
  },
  {
    id: "p1-discharge-date",
    label: "Дата",
    page: 1,
    x: 27.07,
    y: 92.66,
    w: 10.44,
    h: 1.2,
    type: "datetime-date",
    getValue: (d) => d.case.dischargeTime,
    command: "case.setDischargeTime",
    getPayload: (v, d) => ({ uuid: cid(d), timestamp: v }),
  },
  {
    id: "p1-discharge-hospital",
    label: "Евакуйовано до госпіталю",
    page: 1,
    x: 59.55,
    y: 92.7,
    w: 14.0,
    h: 1.2,
    type: "hospital-select",
    getValue: (d) => d.case.dischargeHospitalId,
    command: "case.setDischargeHospital",
    getPayload: (v, d) => ({ uuid: cid(d), hospitalId: v || null }),
  },
  {
    id: "p1-discharge-unit",
    label: "Евакуйовано до підрозділу",
    page: 1,
    x: 74.55,
    y: 92.7,
    w: 8.9,
    h: 1.2,
    type: "unit-autocomplete",
    getValue: (d) => {
      const uid = d.case.dischargeUnitId;
      return { unitId: uid, unitName: d.units?.find((u: any) => u.id === uid)?.name || "" };
    },
    command: "case.setDischargeUnit",
    getPayload: (v, d) => ({ uuid: cid(d), unitId: v || null }),
  },
  ...radioOpts(
    "p1-discharge-role",
    1,
    "case.setDischargeRole",
    (d) => d.case.dischargeRole,
    (v, d) => ({ uuid: cid(d), role: v }),
    [
      { value: "1", x: 86.98, y: 91.97, labelW: 0.75 },
      { value: "2", x: 89.2, y: 91.97, labelW: 0.88 },
      { value: "3", x: 91.68, y: 91.97, labelW: 0.89 },
      { value: "4", x: 94.08, y: 91.97, labelW: 0.92 },
    ],
  ),
  ...radioOpts(
    "p1-evac-cat",
    1,
    "case.setEvacuationCategory",
    (d) => d.case.evacuationCategory,
    (v, d) => ({ uuid: cid(d), evacuationCategory: v }),
    [
      { value: "I", x: 20.72, y: 93.53, labelW: 0.55 },
      { value: "II", x: 22.94, y: 93.53, labelW: 0.98 },
      { value: "III", x: 25.48, y: 93.53, labelW: 1.36 },
    ],
  ),
  arrayToggleCb("p1-discharge-transport-casevac", 1, 37.69, 93.57, "casevac", "case.setTransportTypeItem", "", (c) => c.dischargeTransportType, 5.06, "CASEVAC", { field: "discharge" }),
  arrayToggleCb("p1-discharge-transport-mmpm", 1, 44.55, 93.57, "mmpm", "case.setTransportTypeItem", "", (c) => c.dischargeTransportType, 4.46, "ММПМ", { field: "discharge" }),
  arrayToggleCb("p1-discharge-transport-medevac", 1, 50.9, 93.57, "medevac", "case.setTransportTypeItem", "", (c) => c.dischargeTransportType, 5.95, "MEDEVAC", { field: "discharge" }),
  ...radioOpts(
    "p1-discharge-position",
    1,
    "case.setDischargePosition",
    (d) => d.case.dischargePosition,
    (v, d) => ({ uuid: cid(d), position: v }),
    [
      { value: "sitting", label: "Сидячи", x: 13.47, y: 94.89, labelW: 4.59 },
      { value: "lying", label: "Лежачи", x: 19.85, y: 94.89, labelW: 4.84 },
    ],
  ),
  {
    id: "p1-escort-doctor",
    label: "Б/с",
    page: 1,
    x: 34.9,
    y: 94.93,
    w: CW,
    h: CH,
    type: "checkbox",
    labelW: 2.17,
    getValue: (d) => d.case.escortDoctor,
    command: "case.setEscortDoctor",
    getPayload: (v, d) => ({ uuid: cid(d), escortDoctor: v }),
  },
  {
    id: "p1-escort-nurse",
    label: "М/с",
    page: 1,
    x: 38.56,
    y: 94.93,
    w: CW,
    h: CH,
    type: "checkbox",
    labelW: 2.51,
    getValue: (d) => d.case.escortNurse,
    command: "case.setEscortNurse",
    getPayload: (v, d) => ({ uuid: cid(d), escortNurse: v }),
  },

  // ═══ Discharge equipment (P1) ═══
  {
    id: "p1-discharge-oxygen",
    label: "Кисень",
    page: 1,
    x: 66.85,
    y: 94.93,
    w: CW,
    h: CH,
    type: "checkbox",
    labelW: 4.53,
    getValue: (d) => d.case.dischargeEquipment?.oxygen,
    command: "case.setDischargeEquipmentItem",
    getPayload: (v, d) => ({ uuid: cid(d), key: "oxygen", value: !!v }),
  },
  {
    id: "p1-discharge-ventilator",
    label: "ШВЛ",
    page: 1,
    x: 73.15,
    y: 94.93,
    w: CW,
    h: CH,
    type: "checkbox",
    labelW: 3.15,
    getValue: (d) => d.case.dischargeEquipment?.ventilator,
    command: "case.setDischargeEquipmentItem",
    getPayload: (v, d) => ({ uuid: cid(d), key: "ventilator", value: !!v }),
  },
  {
    id: "p1-discharge-other",
    label: "Інше",
    page: 1,
    x: 77.2,
    y: 94.9,
    w: CW,
    h: CH,
    type: "checkbox",
    labelW: 3.34,
    getValue: (d) => d.case.dischargeEquipment?.other,
    command: "case.setDischargeEquipmentItem",
    getPayload: (v, d) => ({ uuid: cid(d), key: "other", value: !!v }),
  },

  // ═══ Vehicle type A/B/C (P1) ═══
  ...radioOpts(
    "p1-vehicle-type",
    1,
    "case.setDischargeVehicleType",
    (d) => d.case.dischargeVehicleType,
    (v, d) => ({ uuid: cid(d), vehicleType: v }),
    [
      { value: "A", x: 66.38, y: 93.57, labelW: 1.07 },
      { value: "B", x: 69.18, y: 93.57, labelW: 1.04 },
      { value: "C", x: 71.98, y: 93.57, labelW: 1.05 },
      { value: "helicopter", label: "Гелік.", x: 74.89, y: 93.57, labelW: 3 },
      { value: "boat", label: "Катер", x: 79.5, y: 93.57, labelW: 3 },
      { value: "train", label: "Потяг", x: 83.52, y: 93.57, labelW: 3 },
    ],
  ),

  // ═══ Discharge text fields ═══
  // Vehicle number/callsign
  {
    id: "p1-discharge-vehicle-id",
    label: "Номер ТЗ",
    page: 1,
    x: 42.61,
    y: 95.43,
    w: 14.29,
    h: 1,
    type: "text",
    getValue: (d) => d.case.dischargeEquipment?.vehicleId,
    command: "case.setDischargeEquipmentItem",
    getPayload: (v, d) => ({ uuid: cid(d), key: "vehicleId", value: v }),
  },
  // "Інше" equipment detail
  {
    id: "p1-discharge-other-detail",
    label: "Інше",
    page: 1,
    x: 81.9,
    y: 95.54,
    w: 14.29,
    h: 1.3,
    type: "text",
    getValue: (d) => d.case.dischargeEquipment?.otherDetail,
    command: "case.setDischargeEquipmentItem",
    getPayload: (v, d) => ({ uuid: cid(d), key: "otherDetail", value: v }),
  },

  // ═══ Section 14: Medic ═══
  {
    id: "p1-medic-name",
    isPii: true,
    label: "ПІБ МедП",
    page: 1,
    x: 13.61,
    y: 97.56,
    w: 19.74,
    h: 1.2,
    type: "text",
    getValue: (d) => d.case.medicName,
    command: "case.setMedicName",
    getPayload: (v, d) => ({ uuid: cid(d), name: v }),
  },
  {
    id: "p1-medic-facility",
    label: "Медичний підрозділ",
    page: 1,
    x: 64.8,
    y: 97.56,
    w: 17.14,
    h: 1.2,
    type: "facility-autocomplete",
    getValue: (d) => {
      const uid = d.case.medicFacility;
      return { unitId: uid, unitName: d.units?.find((u: any) => u.id === uid)?.name || uid || "" };
    },
    command: "case.setMedicFacility",
    getPayload: (v, d) => ({ uuid: cid(d), facility: v }),
  },
  ...radioOpts(
    "p1-medic-role",
    1,
    "case.setMedicRole",
    (d) => d.case.medicRole,
    (v, d) => ({ uuid: cid(d), role: v }),
    [
      { value: "1", x: 86.98, y: 96.76, labelW: 0.75 },
      { value: "2", x: 89.2, y: 96.76, labelW: 0.88 },
      { value: "3", x: 91.68, y: 96.76, labelW: 0.89 },
      { value: "4", x: 94.08, y: 96.76, labelW: 0.92 },
    ],
  ),

  // ═══ Section 14: Medic Signature (P1) ═══
  {
    id: "p1-signature",
    label: "Підпис",
    page: 1,
    x: 38.78,
    y: 97.36,
    w: 12.24,
    h: 1,
    type: "signature",
    getValue: (d) => d.case.signatureSha256,
    command: "case.setSignatureData",
    getPayload: (v, d) => ({ uuid: cid(d), sha256: v }),
  },

  // ═══════════════════════════════════════════════════════════════════
  // PAGE 2: Role 2 sections
  // ═══════════════════════════════════════════════════════════════════

  // ═══ Section 15: Arrival on Role 2 ═══
  {
    id: "p2-arrival-time",
    label: "Час",
    page: 2,
    x: 20.92,
    y: 5.172,
    w: 5.947,
    h: 1.2,
    type: "time",
    getValue: (d) => d.case.r2ArrivalTime,
    command: "case.setR2ArrivalTime",
    getPayload: (v, d) => ({ uuid: cid(d), timestamp: v }),
  },
  {
    id: "p2-arrival-date",
    label: "Дата",
    page: 2,
    x: 31.4,
    y: 5.172,
    w: 10.61,
    h: 1.2,
    type: "datetime-date",
    getValue: (d) => d.case.r2ArrivalTime,
    command: "case.setR2ArrivalTime",
    getPayload: (v, d) => ({ uuid: cid(d), timestamp: v }),
  },

  // R1 Prior care status — Надавалась / Невідомо / Не надавалась
  ...radioOpts(
    "p2-r1-prior-care",
    2,
    "case.setR1PriorCareStatus",
    (d) => d.case.r1PriorCareStatus,
    (v, d) => ({ uuid: cid(d), status: v }),
    [
      { value: "given", label: "Надавалась", x: 31.53, y: 5.941, labelW: 7.09 },
      { value: "unknown", label: "Невідомо", x: 40.67, y: 5.941, labelW: 5.85 },
      {
        value: "not_given",
        label: "Не надавалась",
        x: 48.36,
        y: 5.941,
        labelW: 8.79,
      },
    ],
  ),

  ...radioOpts(
    "p2-triage",
    2,
    "case.setR2TriageCategory",
    (d) => d.case.r2TriageCategory,
    (v, d) => ({ uuid: cid(d), triageCategory: v }),
    [
      { value: "I", x: 84.14, y: 4.438, labelW: 1.7 },
      { value: "II", x: 86.83, y: 4.438, labelW: 1.9 },
      { value: "III", x: 89.66, y: 4.435, labelW: 2.6 },
      { value: "IV", x: 93.26, y: 4.435, labelW: 2.4 },
    ],
  ),

  // ═══ Section 16: Patient Exam (Page 2) ═══
  ...makeExamFields(),

  // ═══ Section 17: Diagnostics (Page 2) ═══
  ...makeDiagnosticsFields(2),
  ...makeLabValuesFields(),

  // ═══ Section 18: R2 Care ═══
  ...makeR2CareFields(),

  // ═══ Section 18.10-18.12: Surgery Type Checkboxes (Page 2) ═══
  ...makeSurgeryTypeCheckboxes(),

  // ═══ Tourniquets (Page 2) ═══
  ...makeTourniquetRows(2, [
    { limb: "rue" as const, y: 37.03 },
    { limb: "lue" as const, y: 38.36 },
    { limb: "rle" as const, y: 39.72 },
    { limb: "lle" as const, y: 41.08 },
  ]),

  // ═══ Section 18.13: Surgery (Page 2) ═══
  ...makeSurgeryFields(),

  // ═══ Section 19: Vitals (Page 2) ═══
  ...makeVitalsRows(2, [61.8359, 63.2327, 64.5844], [0, 1, 2]),

  // ═══ Section 20: Ventilation (Page 2) ═══
  ...makeVentilationRows(2, [61.8359, 63.2327, 64.5844], [0, 1, 2]),

  // ═══ Section 21: CPR (Page 2) ═══
  {
    id: "p2-cpr-start",
    label: "СЛР Початок",
    page: 2,
    x: 17.78,
    y: 66.48,
    w: 6.326,
    h: 1.2,
    type: "time",
    getValue: (d) => {
      const row = (d.cpr || []).find((c: any) => (c.role ?? 1) === 2);
      return row?.startTime;
    },
    command: "cpr.setStartTime",
    getPayload: (v, d, newUuid) => {
      const row = (d.cpr || []).find((c: any) => (c.role ?? 1) === 2);
      const uuid = newUuid || row?.id;
      return uuid ? { uuid, timestamp: v } : null;
    },
    autoCreate: {
      command: "cpr.create",
      getPayload: (caseId) => ({ caseUuid: caseId, role: 2 }),
    },
  },
  {
    id: "p2-cpr-end",
    label: "СЛР Кінець",
    page: 2,
    x: 17.78,
    y: 67.82,
    w: 6.326,
    h: 1.2,
    type: "time",
    getValue: (d) => {
      const row = (d.cpr || []).find((c: any) => (c.role ?? 1) === 2);
      return row?.endTime;
    },
    command: "cpr.setEndTime",
    getPayload: (v, d, newUuid) => {
      const row = (d.cpr || []).find((c: any) => (c.role ?? 1) === 2);
      const uuid = newUuid || row?.id;
      return uuid ? { uuid, timestamp: v } : null;
    },
    autoCreate: {
      command: "cpr.create",
      getPayload: (caseId) => ({ caseUuid: caseId, role: 2 }),
    },
    minValue: (d) => {
      const row = (d.cpr || []).find((c: any) => (c.role ?? 1) === 2);
      return row?.startTime;
    },
  },

  // ═══ Section 22: Death (Page 2) ═══
  {
    id: "p2-death-circumstances",
    label: "Обставини",
    page: 2,
    x: 25.57,
    y: 67.5,
    w: 70.69,
    h: 2.8,
    type: "textarea",
    lineHeight: 1.5,
    labelW: 0,
    textIndent: 41.8,
    getValue: (d) => d.case.r2DeathCircumstances,
    command: "case.setR2DeathCircumstances",
    getPayload: (v, d) => ({ uuid: cid(d), circumstances: v }),
  },
  {
    id: "p2-death-time",
    label: "Смерть Час",
    page: 2,
    x: 38.36,
    y: 66.34,
    w: 6.326,
    h: 1.2,
    type: "time",
    labelRect: { x: 35.48, y: 65.4, w: 2.937, h: 1.2 },
    getValue: (d) => d.case.r2DeathTime,
    command: "case.setR2DeathTime",
    getPayload: (v, d) => ({ uuid: cid(d), timestamp: v }),
  },
  {
    id: "p2-death-date",
    label: "Смерть Дата",
    page: 2,
    x: 48.84,
    y: 66.34,
    w: 10.6,
    h: 1.2,
    type: "datetime-date",
    labelRect: { x: 45.4, y: 65.34, w: 3.439, h: 1.2 },
    getValue: (d) => d.case.r2DeathTime,
    command: "case.setR2DeathTime",
    getPayload: (v, d) => ({ uuid: cid(d), timestamp: v }),
  },

  // ═══ Section 23: Medications (Page 2) ═══
  ...makeMedicationRows(
    2,
    [72.1116, 73.3742, 74.6368, 75.8995, 77.2654],
    [0, 1, 2, 3, 4],
    "left",
  ),
  ...makeMedicationRows(
    2,
    [72.1116, 73.3742, 74.6368, 75.8995, 77.2654],
    [0, 1, 2, 3, 4],
    "right",
  ),

  // ═══ Blood type (Page 2) ═══
  {
    id: "p2-blood-type",
    label: "Група крові",
    page: 2,
    x: 61.62,
    y: 79.17,
    w: 5.713,
    h: 1.2,
    type: "blood-rh",
    getValue: (d) => d.patient?.bloodType,
    command: "patient.setBloodType",
    getPayload: (v, d) => (pid(d) ? { uuid: pid(d), bloodType: v } : null),
    options: BLOOD_TYPE_OPTIONS,
  },
  {
    id: "p2-rh-factor",
    label: "Rh",
    page: 2,
    x: 69.92,
    y: 79.17,
    w: 5.715,
    h: 1.2,
    type: "blood-rh",
    getValue: (d) => d.patient?.rhFactor,
    command: "patient.setRhFactor",
    getPayload: (v, d) => (pid(d) ? { uuid: pid(d), rhFactor: v } : null),
    options: RH_FACTOR_OPTIONS,
  },
  // "Не визначалась" blood type checkbox (P2)
  {
    id: "p2-blood-unknown",
    label: "Не визначалась",
    page: 2,
    x: 77.61,
    y: 78.4,
    w: CW,
    h: CH,
    type: "checkbox",
    labelW: 9.83,
    getValue: (d) => d.patient?.bloodType === "unknown",
    command: "patient.setBloodType",
    getPayload: (v, d) =>
      pid(d) ? { uuid: pid(d), bloodType: v ? "unknown" : "" } : null,
  },

  // P2 tetanus administered checkbox — left side (section 23.1)
  {
    id: "p2-r2care-tetanusAdministered",
    label: "Профілактика правця",
    page: 2 as const,
    x: 3.864,
    y: 78.4,
    w: CW,
    h: CH,
    type: "checkbox" as const,
    labelW: 13.59,
    getValue: (d: any) => d.case.r2Care?.tetanusAdministered,
    command: "case.setR2CareItem",
    getPayload: (v: any, d: any) => ({
      uuid: cid(d),
      key: "tetanusAdministered",
      value: !!v,
    }),
  },
  // P2 blood transfused checkbox — right side (section 23.2)
  cb(
    "p2-r2care-bloodTransfused",
    2,
    50.6266,
    78.3974,
    "case.setR2CareItem",
    (d: any) => d.case.r2Care?.bloodTransfused,
    (v: any, d: any) => ({ uuid: cid(d), key: "bloodTransfused", value: !!v }),
    9.64,
    "Гемотрансфузія",
  ),

  {
    id: "p2-tetanus",
    label: "Профілактика правця",
    page: 2,
    x: 18.28,
    y: 79.17,
    w: 30.48,
    h: 1.2,
    type: "text",
    getValue: (d) => d.case.r2TetanusProphylaxis,
    command: "case.setR2TetanusProphylaxis",
    getPayload: (v, d) => ({ uuid: cid(d), prophylaxis: v }),
  },

  // ═══ Section 24: Notes (Page 2) ═══
  {
    id: "p2-notes",
    label: "Нотатки",
    page: 2,
    x: 3.8,
    y: 83.95,
    w: 92.42,
    h: 5.08,
    type: "textarea",
    textIndent: 9.75,
    lastLineCutoff: 17,
    lineHeight: 1.25,
    getValue: (d) => d.case.r2Notes,
    command: "case.setR2Notes",
    getPayload: (v, d) => ({ uuid: cid(d), notes: v }),
  },

  // ═══ Section 25: Diagnosis (Page 2) ═══
  {
    id: "p2-diagnosis",
    label: "Діагноз",
    page: 2,
    x: 3.61,
    y: 89.91,
    w: 92.59,
    h: 6.07,
    type: "textarea",
    textIndent: 10,
    lineHeight: 1.2,
    getValue: (d) => d.case.r2Diagnosis,
    command: "case.setR2Diagnosis",
    getPayload: (v, d) => ({ uuid: cid(d), diagnosis: v }),
  },

  // ═══ Kit checkboxes (Page 2, rendered after textareas so they're on top) ═══
  {
    id: "p2-kit1",
    label: "Комплект 1",
    page: 2,
    x: 79.04,
    y: 84,
    w: CW,
    h: CH,
    type: "checkbox",
    labelW: 6.92,
    getValue: (d) => !!d.case.r2Kit1,
    command: "case.setR2Kit1",
    getPayload: (v, d) => ({ uuid: cid(d), kit: v ? "1" : "" }),
  },
  {
    id: "p2-kit2",
    label: "Комплект 2",
    page: 2,
    x: 88.21,
    y: 84,
    w: CW,
    h: CH,
    type: "checkbox",
    labelW: 7.08,
    getValue: (d) => !!d.case.r2Kit2,
    command: "case.setR2Kit2",
    getPayload: (v, d) => ({ uuid: cid(d), kit: v ? "1" : "" }),
  },

  // ═══ Section 26: Discharge (Page 2) ═══
  {
    id: "p2-discharge-time",
    label: "Час",
    page: 2,
    x: 16.76,
    y: 92.66,
    w: 5.947,
    h: 1.2,
    type: "time",
    getValue: (d) => d.case.r2DischargeTime,
    command: "case.setR2DischargeTime",
    getPayload: (v, d) => ({ uuid: cid(d), timestamp: v }),
  },
  {
    id: "p2-discharge-date",
    label: "Дата",
    page: 2,
    x: 27.07,
    y: 92.66,
    w: 10.44,
    h: 1.2,
    type: "datetime-date",
    getValue: (d) => d.case.r2DischargeTime,
    command: "case.setR2DischargeTime",
    getPayload: (v, d) => ({ uuid: cid(d), timestamp: v }),
  },
  {
    id: "p2-discharge-destination",
    label: "Куди",
    page: 2,
    x: 59.55,
    y: 92.7,
    w: 22.9,
    h: 1.2,
    type: "text",
    getValue: (d) => d.case.r2DischargeDestination,
    command: "case.setR2DischargeDestination",
    getPayload: (v, d) => ({ uuid: cid(d), destination: v }),
  },
  // P2 Discharge role — same rect positions as P1
  ...radioOpts(
    "p2-discharge-role",
    2,
    "case.setR2DischargeRole",
    (d) => d.case.r2DischargeRole,
    (v, d) => ({ uuid: cid(d), role: v }),
    [
      { value: "1", x: 86.98, y: 91.97, labelW: 0.75 },
      { value: "2", x: 89.2, y: 91.97, labelW: 0.88 },
      { value: "3", x: 91.68, y: 91.97, labelW: 0.89 },
      { value: "4", x: 94.08, y: 91.97, labelW: 0.92 },
    ],
  ),
  // P2 Evacuation category
  ...radioOpts(
    "p2-evac-cat",
    2,
    "case.setR2EvacuationCategory",
    (d) => d.case.r2EvacuationCategory,
    (v, d) => ({ uuid: cid(d), evacuationCategory: v }),
    [
      { value: "I", x: 20.72, y: 93.53, labelW: 0.55 },
      { value: "II", x: 22.94, y: 93.53, labelW: 0.98 },
      { value: "III", x: 25.48, y: 93.53, labelW: 1.36 },
    ],
  ),
  // P2 Discharge transport
  arrayToggleCb("p2-discharge-transport-casevac", 2, 37.69, 93.57, "casevac", "case.setTransportTypeItem", "", (c) => c.r2DischargeTransportType, 5.06, "CASEVAC", { field: "r2Discharge" }),
  arrayToggleCb("p2-discharge-transport-mmpm", 2, 44.55, 93.57, "mmpm", "case.setTransportTypeItem", "", (c) => c.r2DischargeTransportType, 4.46, "ММПМ", { field: "r2Discharge" }),
  arrayToggleCb("p2-discharge-transport-medevac", 2, 50.9, 93.57, "medevac", "case.setTransportTypeItem", "", (c) => c.r2DischargeTransportType, 5.95, "MEDEVAC", { field: "r2Discharge" }),
  // P2 Position
  ...radioOpts(
    "p2-discharge-position",
    2,
    "case.setR2DischargePosition",
    (d) => d.case.r2DischargePosition,
    (v, d) => ({ uuid: cid(d), position: v }),
    [
      { value: "sitting", label: "Сидячи", x: 13.47, y: 94.89, labelW: 4.59 },
      { value: "lying", label: "Лежачи", x: 19.85, y: 94.89, labelW: 4.84 },
    ],
  ),
  {
    id: "p2-escort-doctor",
    label: "Б/с",
    page: 2,
    x: 34.9,
    y: 94.93,
    w: CW,
    h: CH,
    type: "checkbox",
    labelW: 2.17,
    getValue: (d) => d.case.r2EscortDoctor,
    command: "case.setR2EscortDoctor",
    getPayload: (v, d) => ({ uuid: cid(d), escortDoctor: v }),
  },
  {
    id: "p2-escort-nurse",
    label: "М/с",
    page: 2,
    x: 38.56,
    y: 94.93,
    w: CW,
    h: CH,
    type: "checkbox",
    labelW: 2.51,
    getValue: (d) => d.case.r2EscortNurse,
    command: "case.setR2EscortNurse",
    getPayload: (v, d) => ({ uuid: cid(d), escortNurse: v }),
  },

  // ═══ P2 Discharge equipment ═══
  {
    id: "p2-discharge-oxygen",
    label: "Кисень",
    page: 2,
    x: 66.85,
    y: 94.93,
    w: CW,
    h: CH,
    type: "checkbox",
    labelW: 4.53,
    getValue: (d) => d.case.r2DischargeEquipment?.oxygen,
    command: "case.setR2DischargeEquipmentItem",
    getPayload: (v, d) => ({ uuid: cid(d), key: "oxygen", value: !!v }),
  },
  {
    id: "p2-discharge-ventilator",
    label: "ШВЛ",
    page: 2,
    x: 73.15,
    y: 94.93,
    w: CW,
    h: CH,
    type: "checkbox",
    labelW: 3.15,
    getValue: (d) => d.case.r2DischargeEquipment?.ventilator,
    command: "case.setR2DischargeEquipmentItem",
    getPayload: (v, d) => ({ uuid: cid(d), key: "ventilator", value: !!v }),
  },
  {
    id: "p2-discharge-other",
    label: "Інше",
    page: 2,
    x: 77.2,
    y: 94.9,
    w: CW,
    h: CH,
    type: "checkbox",
    labelW: 3.34,
    getValue: (d) => d.case.r2DischargeEquipment?.other,
    command: "case.setR2DischargeEquipmentItem",
    getPayload: (v, d) => ({ uuid: cid(d), key: "other", value: !!v }),
  },

  // ═══ Vehicle type A/B/C (P2) ═══
  ...radioOpts(
    "p2-vehicle-type",
    2,
    "case.setR2DischargeVehicleType",
    (d) => d.case.r2DischargeVehicleType,
    (v, d) => ({ uuid: cid(d), vehicleType: v }),
    [
      { value: "A", x: 66.38, y: 93.57, labelW: 1.07 },
      { value: "B", x: 69.18, y: 93.57, labelW: 1.04 },
      { value: "C", x: 71.98, y: 93.57, labelW: 1.05 },
      { value: "helicopter", label: "Гелік.", x: 74.89, y: 93.57, labelW: 3 },
      { value: "boat", label: "Катер", x: 79.5, y: 93.57, labelW: 3 },
      { value: "train", label: "Потяг", x: 83.52, y: 93.57, labelW: 3 },
    ],
  ),

  // ═══ P2 Discharge text fields ═══
  {
    id: "p2-discharge-vehicle-id",
    label: "Номер ТЗ",
    page: 2,
    x: 42.61,
    y: 95.43,
    w: 14.29,
    h: 1,
    type: "text",
    getValue: (d) => d.case.r2DischargeEquipment?.vehicleId,
    command: "case.setR2DischargeEquipmentItem",
    getPayload: (v, d) => ({ uuid: cid(d), key: "vehicleId", value: v }),
  },
  {
    id: "p2-discharge-other-detail",
    label: "Інше",
    page: 2,
    x: 81.9,
    y: 95.54,
    w: 14.29,
    h: 1.3,
    type: "text",
    getValue: (d) => d.case.r2DischargeEquipment?.otherDetail,
    command: "case.setR2DischargeEquipmentItem",
    getPayload: (v, d) => ({ uuid: cid(d), key: "otherDetail", value: v }),
  },

  // ═══ Section 27: Medic (Page 2) ═══
  {
    id: "p2-medic-name",
    isPii: true,
    label: "ПІБ МедП",
    page: 2,
    x: 13.45,
    y: 97.56,
    w: 19.91,
    h: 1.2,
    type: "text",
    getValue: (d) => d.case.r2MedicName,
    command: "case.setR2MedicName",
    getPayload: (v, d) => ({ uuid: cid(d), name: v }),
  },
  {
    id: "p2-medic-facility",
    label: "Медичний підрозділ",
    page: 2,
    x: 64.8,
    y: 97.56,
    w: 17.14,
    h: 1.2,
    type: "facility-autocomplete",
    getValue: (d) => {
      const uid = d.case.r2MedicFacility;
      return { unitId: uid, unitName: d.units?.find((u: any) => u.id === uid)?.name || uid || "" };
    },
    command: "case.setR2MedicFacility",
    getPayload: (v, d) => ({ uuid: cid(d), facility: v }),
  },
  // P2 Medic role
  ...radioOpts(
    "p2-medic-role",
    2,
    "case.setR2MedicRole",
    (d) => d.case.r2MedicRole,
    (v, d) => ({ uuid: cid(d), role: v }),
    [
      { value: "1", x: 86.98, y: 96.76, labelW: 0.75 },
      { value: "2", x: 89.2, y: 96.76, labelW: 0.88 },
      { value: "3", x: 91.68, y: 96.76, labelW: 0.89 },
      { value: "4", x: 94.08, y: 96.76, labelW: 0.92 },
    ],
  ),

  // ═══ Section 27: Medic Signature (P2) ═══
  {
    id: "p2-signature",
    label: "Підпис",
    page: 2,
    x: 38.78,
    y: 97.36,
    w: 12.24,
    h: 1,
    type: "signature",
    getValue: (d) => d.case.r2SignatureSha256,
    command: "case.setR2SignatureData",
    getPayload: (v, d) => ({ uuid: cid(d), sha256: v }),
  },
];

// ─── Vitals row generator ────────────────────────────────────────────

function makeVitalsRows(
  page: 1 | 2,
  rowYs: number[],
  indices: number[],
): FieldOverlay[] {
  const role = page as 1 | 2;
  const ROW_H = 1.2;
  const fields: FieldOverlay[] = [];
  const ac = {
    command: "vitals.create",
    getPayload: (caseId: string) => ({ caseUuid: caseId, role }),
  };

  for (const i of indices) {
    const y = rowYs[i];
    const prefix = `p${page}-vitals-${i}`;
    const getRow = (d: any) => {
      const rows = (d.vitals || []).filter(
        (v: any) => (v.role ?? role) === role,
      );
      return rows[i] || null;
    };
    const getUuid = (d: any) => getRow(d)?.id;
    const uid = (d: any, n?: string) => {
      const u = n || getUuid(d);
      return u || null;
    };

    fields.push({
      id: `${prefix}-time`,
      label: "Час",
      page,
      x: 3.34,
      y,
      w: 5.043,
      h: ROW_H,
      type: "time",
      getValue: (d) => getRow(d)?.time,
      command: "vitals.setTime",
      getPayload: (v, d, n) => {
        const u = uid(d, n);
        return u ? { uuid: u, timestamp: v } : null;
      },
      autoCreate: ac,
    });
    fields.push({
      id: `${prefix}-rr`,
      label: "ЧД",
      page,
      x: 8.702,
      y,
      w: 3.333,
      h: ROW_H,
      type: "number",
      getValue: (d) => getRow(d)?.respiratoryRate,
      command: "vitals.setRespiratoryRate",
      getPayload: (v, d, n) => {
        const u = uid(d, n);
        return u ? { uuid: u, value: v } : null;
      },
      autoCreate: ac,
    });
    fields.push({
      id: `${prefix}-spo2`,
      label: "SpO₂",
      page,
      x: 12.37,
      y,
      w: 3.726,
      h: ROW_H,
      type: "number",
      getValue: (d) => getRow(d)?.spO2,
      command: "vitals.setSpO2",
      getPayload: (v, d, n) => {
        const u = uid(d, n);
        return u ? { uuid: u, value: v } : null;
      },
      autoCreate: ac,
    });
    fields.push({
      id: `${prefix}-pulse`,
      label: "Пульс",
      page,
      x: 16.43,
      y,
      w: 4.285,
      h: ROW_H,
      type: "number",
      getValue: (d) => getRow(d)?.pulse,
      command: "vitals.setPulse",
      getPayload: (v, d, n) => {
        const u = uid(d, n);
        return u ? { uuid: u, value: v } : null;
      },
      autoCreate: ac,
    });
    fields.push({
      id: `${prefix}-sys`,
      label: "АТ сист.",
      page,
      x: 21,
      y,
      w: 3.808,
      h: ROW_H,
      type: "number",
      getValue: (d) => getRow(d)?.systolic,
      command: "vitals.setSystolic",
      getPayload: (v, d, n) => {
        const u = uid(d, n);
        return u ? { uuid: u, value: v } : null;
      },
      autoCreate: ac,
    });
    fields.push({
      id: `${prefix}-dia`,
      label: "АТ діаст.",
      page,
      x: 25.58,
      y,
      w: 3.81,
      h: ROW_H,
      type: "number",
      getValue: (d) => getRow(d)?.diastolic,
      command: "vitals.setDiastolic",
      getPayload: (v, d, n) => {
        const u = uid(d, n);
        return u ? { uuid: u, value: v } : null;
      },
      autoCreate: ac,
    });
    fields.push({
      id: `${prefix}-gcs`,
      label: "ШКГ",
      page,
      x: 29.78,
      y,
      w: 5.66,
      h: ROW_H,
      type: "number",
      getValue: (d) => {
        const row = getRow(d);
        if (!row) return null;
        const e = row.gcsEye ?? 0,
          v = row.gcsVerbal ?? 0,
          m = row.gcsMotor ?? 0;
        return e + v + m || null;
      },
      command: "vitals.setGcsEye",
      getPayload: (v, d, n) => {
        const u = uid(d, n);
        return u ? { uuid: u, value: v } : null;
      },
      autoCreate: ac,
    });
    fields.push({
      id: `${prefix}-pain`,
      label: "ШБ",
      page,
      x: 35.78,
      y,
      w: 3.808,
      h: ROW_H,
      type: "number",
      getValue: (d) => getRow(d)?.painScale,
      command: "vitals.setPainScale",
      getPayload: (v, d, n) => {
        const u = uid(d, n);
        return u ? { uuid: u, value: v } : null;
      },
      autoCreate: ac,
    });
    fields.push({
      id: `${prefix}-temp`,
      label: "t°C",
      page,
      x: 39.92,
      y,
      w: 4.035,
      h: ROW_H,
      type: "number",
      getValue: (d) => getRow(d)?.temperature,
      command: "vitals.setTemperature",
      getPayload: (v, d, n) => {
        const u = uid(d, n);
        return u ? { uuid: u, value: v } : null;
      },
      autoCreate: ac,
    });
    fields.push({
      id: `${prefix}-diuresis`,
      label: "Діурез",
      page,
      x: 44.37,
      y,
      w: 4.468,
      h: ROW_H,
      type: "number",
      getValue: (d) => getRow(d)?.diuresis,
      command: "vitals.setDiuresis",
      getPayload: (v, d, n) => {
        const u = uid(d, n);
        return u ? { uuid: u, value: v } : null;
      },
      autoCreate: ac,
    });
  }
  return fields;
}

// ─── Ventilation row generator ───────────────────────────────────────

function makeVentilationRows(
  page: 1 | 2,
  rowYs: number[],
  indices: number[],
): FieldOverlay[] {
  const role = page as 1 | 2;
  const ROW_H = 1.2;
  const fields: FieldOverlay[] = [];
  const ac = {
    command: "ventilation.create",
    getPayload: (caseId: string) => ({ caseUuid: caseId, role }),
  };

  for (const i of indices) {
    const y = rowYs[i];
    const prefix = `p${page}-vent-${i}`;
    const getRow = (d: any) => {
      const rows = (d.ventilation || []).filter(
        (v: any) => (v.role ?? role) === role,
      );
      return rows[i] || null;
    };
    const getUuid = (d: any) => getRow(d)?.id;
    const uid = (d: any, n?: string) => {
      const u = n || getUuid(d);
      return u || null;
    };

    fields.push({
      id: `${prefix}-time`,
      label: "Час",
      page,
      x: 50.89,
      y,
      w: 5.629,
      h: ROW_H,
      type: "time",
      getValue: (d) => getRow(d)?.time,
      command: "ventilation.setTime",
      getPayload: (v, d, n) => {
        const u = uid(d, n);
        return u ? { uuid: u, timestamp: v } : null;
      },
      autoCreate: ac,
    });
    fields.push({
      id: `${prefix}-mode`,
      label: "Режим",
      page,
      x: 56.95,
      y,
      w: 11.37,
      h: ROW_H,
      type: "text",
      getValue: (d) => getRow(d)?.mode,
      command: "ventilation.setMode",
      getPayload: (v, d, n) => {
        const u = uid(d, n);
        return u ? { uuid: u, mode: v } : null;
      },
      autoCreate: ac,
    });
    fields.push({
      id: `${prefix}-rate`,
      label: "ЧД",
      page,
      x: 68.78,
      y,
      w: 4.285,
      h: ROW_H,
      type: "number",
      getValue: (d) => getRow(d)?.rate,
      command: "ventilation.setRate",
      getPayload: (v, d, n) => {
        const u = uid(d, n);
        return u ? { uuid: u, value: v } : null;
      },
      autoCreate: ac,
    });
    fields.push({
      id: `${prefix}-tv`,
      label: "ДО",
      page,
      x: 73.45,
      y,
      w: 4.285,
      h: ROW_H,
      type: "number",
      getValue: (d) => getRow(d)?.tidalVolume,
      command: "ventilation.setTidalVolume",
      getPayload: (v, d, n) => {
        const u = uid(d, n);
        return u ? { uuid: u, value: v } : null;
      },
      autoCreate: ac,
    });
    fields.push({
      id: `${prefix}-fio2`,
      label: "FiO₂",
      page,
      x: 78.24,
      y,
      w: 3.81,
      h: ROW_H,
      type: "number",
      getValue: (d) => getRow(d)?.fiO2,
      command: "ventilation.setFiO2",
      getPayload: (v, d, n) => {
        const u = uid(d, n);
        return u ? { uuid: u, value: v } : null;
      },
      autoCreate: ac,
    });
    fields.push({
      id: `${prefix}-peep`,
      label: "ПТКВ",
      page,
      x: 82.55,
      y,
      w: 3.81,
      h: ROW_H,
      type: "number",
      getValue: (d) => getRow(d)?.peep,
      command: "ventilation.setPeep",
      getPayload: (v, d, n) => {
        const u = uid(d, n);
        return u ? { uuid: u, value: v } : null;
      },
      autoCreate: ac,
    });
    fields.push({
      id: `${prefix}-pip`,
      label: "ПТВ",
      page,
      x: 86.77,
      y,
      w: 3.81,
      h: ROW_H,
      type: "number",
      getValue: (d) => getRow(d)?.pip,
      command: "ventilation.setPip",
      getPayload: (v, d, n) => {
        const u = uid(d, n);
        return u ? { uuid: u, value: v } : null;
      },
      autoCreate: ac,
    });
    fields.push({
      id: `${prefix}-etco2`,
      label: "ETCO₂",
      page,
      x: 90.98,
      y,
      w: 5.171,
      h: ROW_H,
      type: "number",
      getValue: (d) => getRow(d)?.etCO2,
      command: "ventilation.setEtCO2",
      getPayload: (v, d, n) => {
        const u = uid(d, n);
        return u ? { uuid: u, value: v } : null;
      },
      autoCreate: ac,
    });
  }
  return fields;
}

// ─── Medication row generator ────────────────────────────────────────

function makeMedicationRows(
  page: 1 | 2,
  rowYs: number[],
  indices: number[],
  side: "left" | "right",
): FieldOverlay[] {
  // Use distinct roles for left vs right columns: left=page, right=page+10
  const role = side === "left" ? page : page + 10;
  const ROW_H = 1.1;
  const fields: FieldOverlay[] = [];
  const ac = {
    command: "medication.create",
    getPayload: (caseId: string) => ({ caseUuid: caseId, role }),
  };

  const nameX = side === "left" ? 3.8436 : 50.6182;
  const nameW = side === "left" ? 24.197 : 24.1113;
  const doseX = side === "left" ? 28.3598 : 75.2049;
  const doseW = side === "left" ? 7.5931 : 7.6031;
  const routeX = side === "left" ? 36.2838 : 83.1273;
  const routeW = side === "left" ? 6.7397 : 6.7414;
  const timeX = side === "left" ? 43.3594 : 90.2046;
  const timeW = side === "left" ? 5.9468 : 5.9468;

  for (const i of indices) {
    const y = rowYs[i];
    const rowIdx = i; // Each side has its own array, so just use i (0-4)
    const prefix = `p${page}-med-${side}-${i}`;
    const getRow = (d: any) => {
      const rows = (d.medications || []).filter(
        (m: any) => (m.role ?? role) === role,
      );
      return rows[rowIdx] || null;
    };
    const getUuid = (d: any) => getRow(d)?.id;
    const uid = (d: any, n?: string) => {
      const u = n || getUuid(d);
      return u || null;
    };

    fields.push({
      id: `${prefix}-name`,
      label: "Назва",
      page,
      x: nameX,
      y,
      w: nameW,
      h: ROW_H,
      type: "text",
      getValue: (d) => getRow(d)?.name,
      command: "medication.setName",
      getPayload: (v, d, n) => {
        const u = uid(d, n);
        return u ? { uuid: u, name: v } : null;
      },
      autoCreate: ac,
    });
    fields.push({
      id: `${prefix}-dose`,
      label: "Доза/Об'єм",
      page,
      x: doseX,
      y,
      w: doseW,
      h: ROW_H,
      type: "number",
      getValue: (d) => getRow(d)?.dose,
      command: "medication.setDose",
      getPayload: (v, d, n) => {
        const u = uid(d, n);
        return u ? { uuid: u, dose: v } : null;
      },
      autoCreate: ac,
    });
    fields.push({
      id: `${prefix}-route`,
      label: "Шлях",
      page,
      x: routeX,
      y,
      w: routeW,
      h: ROW_H,
      type: "text",
      getValue: (d) => getRow(d)?.route,
      command: "medication.setRoute",
      getPayload: (v, d, n) => {
        const u = uid(d, n);
        return u ? { uuid: u, route: v } : null;
      },
      autoCreate: ac,
    });
    fields.push({
      id: `${prefix}-time`,
      label: "Час",
      page,
      x: timeX,
      y,
      w: timeW,
      h: ROW_H,
      type: "time",
      getValue: (d) => getRow(d)?.time,
      command: "medication.setTime",
      getPayload: (v, d, n) => {
        const u = uid(d, n);
        return u ? { uuid: u, timestamp: v } : null;
      },
      autoCreate: ac,
    });
  }
  return fields;
}

// ─── Tourniquet row generator ────────────────────────────────────────
// P1 colons: Накл. 12.1/Зняття 16.8/Конв. 21.6/Перем. 26.3
// P2 colons at 16.6

function makeTourniquetRows(
  page: 1 | 2,
  rows: Array<{ limb: "rue" | "lue" | "rle" | "lle"; y: number }>,
): FieldOverlay[] {
  const role = page as 1 | 2;
  const ROW_H = 1.1;
  const fields: FieldOverlay[] = [];

  const limbLabels: Record<string, string> = {
    rue: "ПВК",
    lue: "ЛВК",
    rle: "ПНК",
    lle: "ЛНК",
  };
  for (const row of rows) {
    const prefix = `p${page}-tq-${row.limb}`;
    const ll = limbLabels[row.limb];
    const getRow = (d: any) =>
      (d.tourniquets || []).find(
        (t: any) => (t.role ?? role) === role && t.limb === row.limb,
      ) || null;
    const getUuid = (d: any) => getRow(d)?.id;
    // Include limb in autoCreate so the row can be found after sync
    const ac = {
      command: "tourniquet.create",
      getPayload: (caseId: string) => ({
        caseUuid: caseId,
        role,
        limb: row.limb,
      }),
    };

    if (page === 1) {
      fields.push({
        id: `${prefix}-app`,
        label: `Накл. ${ll}`,
        page,
        x: 10.13,
        y: row.y,
        w: 4.216,
        h: ROW_H,
        type: "time",
        getValue: (d) => getRow(d)?.applicationTime,
        command: "tourniquet.setApplicationTime",
        getPayload: (v, d, n) => {
          const u = n || getUuid(d);
          return u ? { uuid: u, timestamp: v } : null;
        },
        autoCreate: ac,
      });
      fields.push({
        id: `${prefix}-rem`,
        label: `Зняття ${ll}`,
        page,
        x: 14.91,
        y: row.y,
        w: 4.222,
        h: ROW_H,
        type: "time",
        getValue: (d) => getRow(d)?.removalTime,
        command: "tourniquet.setRemovalTime",
        getPayload: (v, d, n) => {
          const u = n || getUuid(d);
          return u ? { uuid: u, timestamp: v } : null;
        },
        autoCreate: ac,
      });
      fields.push({
        id: `${prefix}-conv`,
        label: `Конв. ${ll}`,
        page,
        x: 19.71,
        y: row.y,
        w: 4.222,
        h: ROW_H,
        type: "time",
        getValue: (d) => getRow(d)?.conversionTime,
        command: "tourniquet.setConversionTime",
        getPayload: (v, d, n) => {
          const u = n || getUuid(d);
          return u ? { uuid: u, timestamp: v } : null;
        },
        autoCreate: ac,
      });
      fields.push({
        id: `${prefix}-repo`,
        label: `Перем. ${ll}`,
        page,
        x: 24.37,
        y: row.y,
        w: 4.139,
        h: ROW_H,
        type: "time",
        getValue: (d) => getRow(d)?.repositioningTime,
        command: "tourniquet.setRepositioningTime",
        getPayload: (v, d, n) => {
          const u = n || getUuid(d);
          return u ? { uuid: u, timestamp: v } : null;
        },
        autoCreate: ac,
      });
    } else {
      fields.push({
        id: `${prefix}-app`,
        label: `Накл. ${ll}`,
        page,
        x: 7.753,
        y: row.y,
        w: 6.669,
        h: ROW_H,
        type: "time",
        getValue: (d) => getRow(d)?.applicationTime,
        command: "tourniquet.setApplicationTime",
        getPayload: (v, d, n) => {
          const u = n || getUuid(d);
          return u ? { uuid: u, timestamp: v } : null;
        },
        autoCreate: ac,
      });
      fields.push({
        id: `${prefix}-rem`,
        label: `Зняття ${ll}`,
        page,
        x: 14.68,
        y: row.y,
        w: 4.309,
        h: ROW_H,
        type: "time",
        getValue: (d) => getRow(d)?.removalTime,
        command: "tourniquet.setRemovalTime",
        getPayload: (v, d, n) => {
          const u = n || getUuid(d);
          return u ? { uuid: u, timestamp: v } : null;
        },
        autoCreate: ac,
      });
    }
  }
  return fields;
}

// ─── Checkbox helper ─────────────────────────────────────────────────

function cb(
  id: string,
  page: 1 | 2,
  x: number,
  y: number,
  command: string,
  getValue: (d: any) => any,
  getPayload: (v: any, d: any) => Record<string, unknown> | null,
  labelW = 7,
  label?: string,
): FieldOverlay {
  return {
    id,
    page,
    x,
    y,
    w: CW,
    h: CH,
    type: "checkbox",
    getValue,
    command,
    getPayload,
    labelW,
    ...(label ? { label } : {}),
  };
}

// ─── Prior care checkboxes (Section 2.4) ─────────────────────────────

function makePriorCareFields(): FieldOverlay[] {
  const items: Array<{
    key: string;
    x: number;
    y: number;
    labelW?: number;
    label?: string;
  }> = [
    // 2.3 Assessment — checkboxes
    { key: "assessWarm", x: 61.21, y: 16.75, labelW: 5.18, label: "Теплова" },
    { key: "assessCold", x: 68.72, y: 16.75, labelW: 5.99, label: "Холодова" },
    { key: "bandage", x: 3.796, y: 19.25, labelW: 5.64, label: "Пов'язка" },
    { key: "packing", x: 11.2, y: 19.25, labelW: 8.77, label: "Тампонування" },
    { key: "oxygen", x: 21.96, y: 19.25, labelW: 1.5, label: "O₂" },
    { key: "ofp", x: 25.41, y: 19.25, labelW: 3.05, label: "ОФП" },
    { key: "nfp", x: 3.796, y: 20.47, labelW: 3.06, label: "НФП" },
    { key: "ngp", x: 8.668, y: 20.47, labelW: 2.7, label: "НГП" },
    { key: "ett", x: 13.32, y: 20.47, labelW: 2.42, label: "ETT" },
    {
      key: "cricothyrotomy",
      x: 17.7,
      y: 20.47,
      labelW: 10.13,
      label: "Крікотиреотомія",
    },
    {
      key: "needleDecompression",
      x: 3.796,
      y: 21.7,
      labelW: 13.08,
      label: "Голкова декомпресія",
    },
    {
      key: "occlusiveDressing",
      x: 18,
      y: 21.7,
      labelW: 11.67,
      label: "Оклюзійна наліпка",
    },
    { key: "bvm", x: 3.796, y: 22.92, labelW: 11.08, label: "Дихальний мішок" },
    { key: "ivAccess", x: 22.38, y: 22.92, labelW: 2.07, label: "в/в" },
    { key: "ioAccess", x: 26.27, y: 22.94, labelW: 2.08, label: "в/к" },
    {
      key: "hypothermiaPrevention",
      x: 3.796,
      y: 24.23,
      labelW: 14.85,
      label: "Профілактика гіпотермії",
    },
    {
      key: "immobilization",
      x: 20.72,
      y: 24.23,
      labelW: 8.19,
      label: "Іммобілізація",
    },
    {
      key: "eyeShield",
      x: 3.796,
      y: 25.52,
      labelW: 8.66,
      label: "Щиток на око",
    },
    { key: "other", x: 14.01, y: 25.55, labelW: 3.06, label: "Інше" },
    // 2.5 Tourniquet limb indicators
    { key: "tourniquetRUE", x: 31.9, y: 20.47, labelW: 2.75, label: "ПВК" },
    { key: "tourniquetLUE", x: 31.9, y: 21.7, labelW: 3.09, label: "ЛВК" },
    { key: "tourniquetRLE", x: 31.9, y: 22.92, labelW: 3.2, label: "ПНК" },
    { key: "tourniquetLLE", x: 31.9, y: 24.23, labelW: 2.86, label: "ЛНК" },
    { key: "tourniquetAT", x: 31.9, y: 25.52, labelW: 1.73, label: "АТ" },
    { key: "tourniquetVT", x: 34.98, y: 25.52, labelW: 1.77, label: "ВТ" },
    // 2.6 Medications
    {
      key: "medPillPack",
      x: 3.845,
      y: 28.09,
      labelW: 21.93,
      label: "Набір ліків",
    },
    { key: "medTXA", x: 3.845, y: 29.31, labelW: 7.5, label: "ТКК" },
    {
      key: "medAnalgesic",
      x: 3.845,
      y: 30.62,
      labelW: 6.91,
      label: "Анальгетик",
    },
    {
      key: "medAntibiotic",
      x: 3.845,
      y: 31.97,
      labelW: 6.77,
      label: "Антибіотик",
    },
  ];

  const checkboxFields = items.map((item) =>
    cb(
      `p1-prior-${item.key}`,
      1,
      item.x,
      item.y,
      "case.setPriorCareItem",
      (d: any) => d.case.priorCare?.[item.key],
      (v: any, d: any) => ({
        uuid: cid(d),
        key: item.key,
        value: !!v,
      }),
      item.labelW,
      item.label,
    ),
  );

  // ── 2.5 Tourniquet time grid ──
  const tqCols = [
    { suffix: "App", x: 38.23, label: "Накл." },
    { suffix: "Rem", x: 43.77, label: "Зняття" },
    { suffix: "Conv", x: 49.33, label: "Конв." },
    { suffix: "Repo", x: 54.72, label: "Перем." },
  ];
  const tqRows = [
    { limb: "RUE", y: 21.09, label: "ПВК" },
    { limb: "LUE", y: 22.32, label: "ЛВК" },
    { limb: "RLE", y: 23.54, label: "ПНК" },
    { limb: "LLE", y: 24.85, label: "ЛНК" },
  ];
  const tqFields: FieldOverlay[] = [];
  for (const row of tqRows) {
    for (const col of tqCols) {
      const key = `tourniquet${row.limb}${col.suffix}`;
      tqFields.push({
        id: `p1-prior-${key}`,
        label: `${col.label} ${row.label}`,
        page: 1,
        x: col.x,
        y: row.y,
        w: 4.9,
        h: 1.2,
        type: "time",
        getValue: (d: any) => d.case.priorCare?.[key],
        command: "case.setPriorCareItem",
        getPayload: (v: any, d: any) => ({ uuid: cid(d), key, value: v }),
      });
    }
  }
  // AT row — only Накл. and Зняття columns
  for (const col of tqCols.slice(0, 2)) {
    const key = `tourniquetAT${col.suffix}`;
    tqFields.push({
      id: `p1-prior-${key}`,
      label: `${col.label} АТ`,
      page: 1,
      x: col.x,
      y: 26.14,
      w: 4.9,
      h: 1.2,
      type: "time",
      getValue: (d: any) => d.case.priorCare?.[key],
      command: "case.setPriorCareItem",
      getPayload: (v: any, d: any) => ({ uuid: cid(d), key, value: v }),
    });
  }

  // ── 2.6 Medication detail grid ──
  const medRows: Array<{
    key: string;
    y: number;
    label: string;
    detailX?: number;
    detailW?: number;
    noGrid?: boolean;
  }> = [
    { key: "medPillPack", y: 28.51, label: "Набору ліків", noGrid: true },
    { key: "medTXA", y: 29.93, label: "ТКК" },
    {
      key: "medAnalgesic",
      y: 31.35,
      detailX: 12.07,
      detailW: 29.33,
      label: "Анальгетика",
    },
    {
      key: "medAntibiotic",
      y: 32.7,
      detailX: 12.02,
      detailW: 29.38,
      label: "Антибіотика",
    },
    { key: "medExtra", y: 34.05, detailX: 3.8, detailW: 37.6, label: "Іншого" },
  ];
  const medFields: FieldOverlay[] = [];
  for (const row of medRows) {
    if (row.detailX != null) {
      const dKey = `${row.key}Detail`;
      medFields.push({
        id: `p1-prior-${dKey}`,
        label: `Назва ${row.label}`,
        page: 1,
        x: row.detailX,
        y: row.y,
        w: row.detailW!,
        h: 1.2,
        type: "text",
        getValue: (d: any) => d.case.priorCare?.[dKey],
        command: "case.setPriorCareItem",
        getPayload: (v: any, d: any) => ({ uuid: cid(d), key: dKey, value: v }),
      });
    }
    if (!row.noGrid) {
      const doseKey = `${row.key}Dose`;
      medFields.push({
        id: `p1-prior-${doseKey}`,
        label: `Доза ${row.label}`,
        page: 1,
        x: 42.1,
        y: row.y,
        w: 5.7,
        h: 1.2,
        type: "number",
        getValue: (d: any) => d.case.priorCare?.[doseKey],
        command: "case.setPriorCareItem",
        getPayload: (v: any, d: any) => ({
          uuid: cid(d),
          key: doseKey,
          value: v,
        }),
      });
      const routeKey = `${row.key}Route`;
      medFields.push({
        id: `p1-prior-${routeKey}`,
        label: `Шлях ${row.label}`,
        page: 1,
        x: 48.4,
        y: row.y,
        w: 5.7,
        h: 1.2,
        type: "text",
        getValue: (d: any) => d.case.priorCare?.[routeKey],
        command: "case.setPriorCareItem",
        getPayload: (v: any, d: any) => ({
          uuid: cid(d),
          key: routeKey,
          value: v,
        }),
      });
      const timeKey = `${row.key}Time`;
      medFields.push({
        id: `p1-prior-${timeKey}`,
        label: `Час ${row.label}`,
        page: 1,
        x: 54.7,
        y: row.y,
        w: 4.9,
        h: 1.2,
        type: "time",
        getValue: (d: any) => d.case.priorCare?.[timeKey],
        command: "case.setPriorCareItem",
        getPayload: (v: any, d: any) => ({
          uuid: cid(d),
          key: timeKey,
          value: v,
        }),
      });
    }
  }

  // ── 2.3 Assessment vitals ──
  const assessY = 17.42;
  const assessFields: FieldOverlay[] = [
    {
      id: "p1-prior-assessRR",
      label: "ЧД",
      page: 1,
      x: 5.81,
      y: assessY,
      w: 2.85,
      h: 1.2,
      type: "number",
      getValue: (d: any) => d.case.priorCare?.assessRR,
      command: "case.setPriorCareItem",
      getPayload: (v: any, d: any) => ({
        uuid: cid(d),
        key: "assessRR",
        value: v,
      }),
    },
    {
      id: "p1-prior-assessSpO2",
      label: "SpO₂",
      page: 1,
      x: 14.03,
      y: assessY,
      w: 3.81,
      h: 1.2,
      type: "number",
      getValue: (d: any) => d.case.priorCare?.assessSpO2,
      command: "case.setPriorCareItem",
      getPayload: (v: any, d: any) => ({
        uuid: cid(d),
        key: "assessSpO2",
        value: v,
      }),
    },
    {
      id: "p1-prior-assessPulse",
      label: "Пульс",
      page: 1,
      x: 23.13,
      y: assessY,
      w: 2.86,
      h: 1.2,
      type: "number",
      getValue: (d: any) => d.case.priorCare?.assessPulse,
      command: "case.setPriorCareItem",
      getPayload: (v: any, d: any) => ({
        uuid: cid(d),
        key: "assessPulse",
        value: v,
      }),
    },
    {
      id: "p1-prior-assessSystolic",
      label: "АТ сист.",
      page: 1,
      x: 28.68,
      y: assessY,
      w: 4.76,
      h: 1.2,
      type: "number",
      getValue: (d: any) => d.case.priorCare?.assessSystolic,
      command: "case.setPriorCareItem",
      getPayload: (v: any, d: any) => ({
        uuid: cid(d),
        key: "assessSystolic",
        value: v,
      }),
    },
    {
      id: "p1-prior-assessDiastolic",
      label: "АТ діаст.",
      page: 1,
      x: 34.34,
      y: assessY,
      w: 4.76,
      h: 1.2,
      type: "number",
      getValue: (d: any) => d.case.priorCare?.assessDiastolic,
      command: "case.setPriorCareItem",
      getPayload: (v: any, d: any) => ({
        uuid: cid(d),
        key: "assessDiastolic",
        value: v,
      }),
    },
    {
      id: "p1-prior-assessAVPU",
      label: "AVPU",
      page: 1,
      x: 43.94,
      y: assessY,
      w: 2.86,
      h: 1.2,
      type: "text",
      getValue: (d: any) => d.case.priorCare?.assessAVPU,
      command: "case.setPriorCareItem",
      getPayload: (v: any, d: any) => ({
        uuid: cid(d),
        key: "assessAVPU",
        value: v,
      }),
    },
    {
      id: "p1-prior-assessPain",
      label: "ШБ",
      page: 1,
      x: 55.82,
      y: assessY,
      w: 3.79,
      h: 1.2,
      type: "number",
      getValue: (d: any) => d.case.priorCare?.assessPain,
      command: "case.setPriorCareItem",
      getPayload: (v: any, d: any) => ({
        uuid: cid(d),
        key: "assessPain",
        value: v,
      }),
    },
  ];

  // ── 2.4 Prior care text fields ──
  const priorTextFields: FieldOverlay[] = [
    {
      id: "p1-prior-otherDetail",
      label: "Інше",
      page: 1,
      x: 18.4,
      y: 26.2,
      w: 12.4,
      h: 1.2,
      type: "text",
      getValue: (d: any) => d.case.priorCare?.otherDetail,
      command: "case.setPriorCareItem",
      getPayload: (v: any, d: any) => ({
        uuid: cid(d),
        key: "otherDetail",
        value: v,
      }),
    },
    // Tourniquet detail (AT/VT area)
    {
      id: "p1-prior-tourniquetDetail",
      label: "Локалізація",
      page: 1,
      x: 50.8,
      y: 26.2,
      w: 8.83,
      h: 1.2,
      type: "text",
      getValue: (d: any) => d.case.priorCare?.tourniquetDetail,
      command: "case.setPriorCareItem",
      getPayload: (v: any, d: any) => ({
        uuid: cid(d),
        key: "tourniquetDetail",
        value: v,
      }),
    },
  ];

  // ── 2.2 Provider radio group ──
  const providerFields = radioOpts(
    "p1-prior-provider",
    1,
    "case.setPriorCareItem",
    (d: any) => d.case.priorCare?.provider,
    (v: any, d: any) => ({ uuid: cid(d), key: "provider", value: v }),
    [
      { value: "self", label: "СД", x: 29.09, y: 14.27, labelW: 1.94 },
      { value: "nonPro", label: "НП", x: 32.48, y: 14.27, labelW: 2.05 },
      { value: "medPro", label: "МедП", x: 36.1, y: 14.27, labelW: 3.71 },
      { value: "unknown", label: "Невідомо", x: 41.46, y: 14.27, labelW: 5.93 },
      {
        value: "none",
        label: "Не надавалась",
        x: 48.89,
        y: 14.27,
        labelW: 8.93,
      },
    ],
  );

  return [
    ...checkboxFields,
    ...assessFields,
    ...tqFields,
    ...medFields,
    ...priorTextFields,
    ...providerFields,
  ];
}

// ─── Section 3: Trauma / Illness / CBRN checkboxes ───────────────────
// Each group dispatches its own command with the correct array payload.

/** Toggle helper: toggle a single item via an item-based command. */
function arrayToggleCb(
  id: string,
  page: 1 | 2,
  x: number,
  y: number,
  enumValue: string,
  command: string,
  _arrayField: string,
  caseGetter: (c: any) => string[] | null,
  labelW = 7,
  label?: string,
  extraPayload?: Record<string, unknown>,
): FieldOverlay {
  return {
    id,
    page,
    x,
    y,
    w: CW,
    h: CH,
    type: "checkbox",
    getValue: (d: any) => (caseGetter(d.case) || []).includes(enumValue),
    command,
    getPayload: (v: any, d: any) => ({
      uuid: cid(d),
      key: enumValue,
      value: !!v,
      ...extraPayload,
    }),
    labelW,
    ...(label ? { label } : {}),
  };
}

function makeTraumaFields(): FieldOverlay[] {
  // Key = PDF checkbox key, value = TraumaType enum value
  const traumaItems: Array<{
    key: string;
    enumVal: string;
    x: number;
    y: number;
    labelW?: number;
    label?: string;
  }> = [
    {
      key: "gunshot",
      enumVal: "gunshot",
      x: 61.21,
      y: 14.27,
      labelW: 7.98,
      label: "Вогнепальна",
    },
    {
      key: "blast",
      enumVal: "explosive",
      x: 71.53,
      y: 14.27,
      labelW: 5.86,
      label: "Вибухова",
    },
    {
      key: "blunt",
      enumVal: "blunt",
      x: 79.74,
      y: 14.27,
      labelW: 3.08,
      label: "Тупа",
    },
    {
      key: "open",
      enumVal: "open",
      x: 85.15,
      y: 14.27,
      labelW: 5.58,
      label: "Відкрита",
    },
    {
      key: "crush",
      enumVal: "crush",
      x: 61.21,
      y: 15.53,
      labelW: 11.67,
      label: "Тривале стиснення",
    },
  ];

  const traumaFields = traumaItems.map((item) =>
    arrayToggleCb(
      `p1-trauma-${item.key}`,
      1,
      item.x,
      item.y,
      item.enumVal,
      "case.setTraumaTypeItem",
      "traumaTypes",
      (c) => c.traumaTypes as string[] | null,
      item.labelW,
      item.label,
    ),
  );

  const penetratingField = cb(
    "p1-trauma-penetrating",
    1,
    78.8234,
    15.5317,
    "case.setTraumaPenetrating",
    (d: any) => !!d.case.traumaPenetrating,
    (v: any, d: any) => ({ uuid: cid(d), penetrating: !!v }),
    6.32,
    "Проникаюча",
  );
  const nonPenetratingField = cb(
    "p1-trauma-nonPenetrating",
    1,
    87.3438,
    15.5317,
    "case.setTraumaNonPenetrating",
    (d: any) => !!d.case.traumaNonPenetrating,
    (v: any, d: any) => ({ uuid: cid(d), value: !!v }),
    7.75,
    "Непроникаюча",
  );

  const conditionItems: Array<{
    key: string;
    enumVal: string;
    x: number;
    y: number;
    labelW?: number;
    label?: string;
  }> = [
    {
      key: "somatic",
      enumVal: "somatic",
      x: 61.21,
      y: 19.25,
      labelW: 6.65,
      label: "Соматична",
    },
    {
      key: "infectious",
      enumVal: "infectious",
      x: 61.21,
      y: 20.6,
      labelW: 6.89,
      label: "Інфекційна",
    },
    {
      key: "mentalDisorder",
      enumVal: "psychiatric",
      x: 61.21,
      y: 21.94,
      label: "Розлади психіки",
    },
    {
      key: "poisoning",
      enumVal: "poisoning",
      x: 61.21,
      y: 24.07,
      labelW: 5.82,
      label: "Отруєння",
    },
  ];

  const conditionFields = conditionItems.map((item) => {
    const f = arrayToggleCb(
      `p1-condition-${item.key}`,
      1,
      item.x,
      item.y,
      item.enumVal,
      "case.setConditionTypeItem",
      "conditionTypes",
      (c) => c.conditionTypes as string[] | null,
      item.labelW,
      item.label,
    );
    if (item.key === "mentalDisorder") {
      f.labelRect = { x: 62.37, y: 21.9, w: 9.71, h: 1.66 };
    }
    return f;
  });

  const cbrnItems: Array<{
    key: string;
    x: number;
    y: number;
    labelW?: number;
    label?: string;
  }> = [
    { key: "chemical", x: 61.93, y: 28.16, labelW: 4.58, label: "Хімічне" },
    {
      key: "biological",
      x: 61.93,
      y: 29.33,
      labelW: 6.36,
      label: "Біологічне",
    },
    {
      key: "radiological",
      x: 61.93,
      y: 30.5,
      labelW: 7.73,
      label: "Радіологічне",
    },
    { key: "nuclear", x: 61.93, y: 31.67, labelW: 4.24, label: "Ядерне" },
  ];

  const cbrnFields = cbrnItems.map((item) =>
    arrayToggleCb(
      `p1-cbrn-${item.key}`,
      1,
      item.x,
      item.y,
      item.key,
      "case.setCbrnItem",
      "cbrnTypes",
      (c) => c.cbrn as string[] | null,
      item.labelW,
      item.label,
    ),
  );

  const traumaNotesField: FieldOverlay = {
    id: "p1-trauma-notes",
    label: "Нотатки",
    page: 1,
    x: 3.71,
    y: 38.31,
    w: 92.31,
    h: 2.7,
    type: "textarea",
    textIndent: 5.46,
    lineHeight: 1.38,
    getValue: (d: any) => d.case.priorCare?.traumaNotes,
    command: "case.setPriorCareItem",
    getPayload: (v: any, d: any) => ({
      uuid: cid(d),
      key: "traumaNotes",
      value: v,
    }),
  };

  return [
    ...traumaFields,
    penetratingField,
    nonPenetratingField,
    ...conditionFields,
    ...cbrnFields,
    traumaNotesField,
  ];
}

// ─── R1 Care checkboxes (Section 4) ─────────────────────────────────

function makeR1CareFields(): FieldOverlay[] {
  const items: Array<{
    key: string;
    x: number;
    y: number;
    labelW?: number;
    label?: string;
  }> = [
    { key: "sd", x: 30.68, y: 41.97, labelW: 1.94, label: "СД" },
    { key: "nfp", x: 34.37, y: 41.97, labelW: 3.07, label: "НФП" },
    { key: "ofp", x: 39.36, y: 41.97, labelW: 3.05, label: "ОФП" },
    { key: "ngp", x: 44.27, y: 41.97, labelW: 2.7, label: "НГП" },
    { key: "ett", x: 30.68, y: 43.26, labelW: 2.43, label: "ETT" },
    {
      key: "cricothyrotomy",
      x: 38.24,
      y: 43.26,
      labelW: 10.47,
      label: "Крікотиреотомія",
    },
    { key: "shvl", x: 42.7, y: 45.77, labelW: 3.17, label: "ШВЛ" },
    { key: "thoracocentesis", x: 30.68, y: 45.77, labelW: 1.65, label: "O₂" },
    { key: "pvd", x: 56.71, y: 41.97, labelW: 2.81, label: "ПВД" },
    { key: "cvd", x: 61.43, y: 41.97, labelW: 2.81, label: "ЦВД" },
    { key: "ioShoulder", x: 53.76, y: 43.26, labelW: 3.77, label: "в/к плече" },
    { key: "ioTibia", x: 59.27, y: 43.26, labelW: 4.78, label: "в/к г/к" },
    {
      key: "ioSternum",
      x: 65.82,
      y: 43.26,
      labelW: 5.07,
      label: "в/к грудина",
    },
    { key: "hypothermia", x: 51, y: 47.12, labelW: 1.09, label: "Гіпотермія" },
    {
      key: "immobilization",
      x: 51,
      y: 50.95,
      labelW: 3.06,
      label: "Іммобілізація",
    },
    {
      key: "urinaryCatheter",
      x: 76.58,
      y: 41.97,
      labelW: 10.42,
      label: "Сечовий катетер",
    },
    { key: "reboa", x: 89.67, y: 41.97, labelW: 4.22, label: "REBOA" },
    {
      key: "gastricTube",
      x: 76.58,
      y: 43.26,
      labelW: 10.57,
      label: "Шлунковий зонд",
    },
    {
      key: "decontamination",
      x: 76.58,
      y: 45.65,
      labelW: 9.53,
      label: "Деконтамінація",
    },
    { key: "anesthesia", x: 83.08, y: 48.14, labelW: 5.94, label: "Анестезія" },
    { key: "bandage", x: 3.864, y: 50.95, labelW: 5.3, label: "Пов'язка" },
    {
      key: "pressureBandage",
      x: 10.84,
      y: 50.95,
      labelW: 10.2,
      label: "Тиснуча пов'язка",
    },
    {
      key: "woundCare",
      x: 22.74,
      y: 50.95,
      labelW: 6.5185,
      label: "Рани/опіки",
    },
    { key: "redressing", x: 3.864, y: 52.24, labelW: 6.7, label: "Перев'язка" },
    { key: "packing", x: 12.26, y: 52.24, labelW: 8.77, label: "Тампонування" },
    { key: "occlusiveRight", x: 42.62, y: 50.95, labelW: 1.16, label: "П" },
    { key: "occlusiveLeft", x: 45.14, y: 50.95, labelW: 1.16, label: "Л" },
    { key: "needleDecompRight", x: 43.59, y: 52.24, labelW: 1.16, label: "П" },
    { key: "needleDecompLeft", x: 46.1, y: 52.24, labelW: 1.16, label: "Л" },
    { key: "eyeShieldRight", x: 85.56, y: 52.26, labelW: 1.11, label: "П" },
    { key: "eyeShieldLeft", x: 87.95, y: 52.26, labelW: 1.11, label: "Л" },
    // 4.1 Tourniquet limb indicators
    { key: "tourniquetRUE", x: 3.796, y: 43.22, labelW: 2.75, label: "ПВК" },
    { key: "tourniquetLUE", x: 3.796, y: 44.46, labelW: 3.09, label: "ЛВК" },
    { key: "tourniquetRLE", x: 3.796, y: 45.67, labelW: 3.2, label: "ПНК" },
    { key: "tourniquetLLE", x: 3.796, y: 46.98, labelW: 2.86, label: "ЛНК" },
    { key: "tourniquetAT", x: 3.796, y: 48.27, labelW: 1.73, label: "АТ" },
    { key: "tourniquetVT", x: 6.883, y: 48.27, labelW: 1.77, label: "ВТ" },
    // 4.6 Hypothermia active/passive
    { key: "hypothermiaActive", x: 51, y: 47.12, labelW: 1.09, label: "А" },
    { key: "hypothermiaPassive", x: 53.48, y: 47.12, labelW: 1.16, label: "П" },
    // 4.7 Immobilization sub-types
    { key: "immobCervical", x: 51, y: 50.95, labelW: 3.06, label: "ШВХ" },
    { key: "immobSpine", x: 55.95, y: 50.95, labelW: 4.39, label: "Хребет" },
    { key: "immobPelvis", x: 62.24, y: 50.97, labelW: 2.12, label: "Таз" },
    { key: "immobRUE", x: 51, y: 52.26, labelW: 2.75, label: "ПВК" },
    { key: "immobLUE", x: 55.54, y: 52.24, labelW: 2.75, label: "ЛВК" },
    { key: "immobRLE", x: 60.19, y: 52.24, labelW: 2.86, label: "ПНК" },
    { key: "immobLLE", x: 65, y: 52.24, labelW: 2.86, label: "ЛНК" },
    // 4.10 Regional anesthesia
    {
      key: "anesthesiaRegional",
      x: 76.58,
      y: 49.31,
      labelW: 6.82,
      label: "Регіонарна",
    },
    // 4.4 Thoracocentesis findings
    {
      key: "thoracoRightBlood",
      x: 31.94,
      y: 48.38,
      labelW: 3.21,
      label: "П Кров",
    },
    {
      key: "thoracoRightAir",
      x: 41.41,
      y: 48.38,
      labelW: 4.84,
      label: "П Повітря",
    },
    {
      key: "thoracoLeftBlood",
      x: 31.94,
      y: 49.66,
      labelW: 3.21,
      label: "Л Кров",
    },
    {
      key: "thoracoLeftAir",
      x: 41.41,
      y: 49.66,
      labelW: 4.84,
      label: "Л Повітря",
    },
  ];

  const checkboxes = items.map((item) =>
    cb(
      `p1-r1care-${item.key}`,
      1,
      item.x,
      item.y,
      "case.setR1CareItem",
      (d: any) => d.case.r1Care?.[item.key],
      (v: any, d: any) => ({
        uuid: cid(d),
        key: item.key,
        value: !!v,
      }),
      item.labelW,
      item.label,
    ),
  );
  // labelRect overrides
  const wc = checkboxes.find((f) => f.id === "p1-r1care-woundCare");
  if (wc) wc.labelRect = { x: 22.74, y: 50.95, w: 6.519, h: 1.645 };

  // R1 care text fields
  const r1TextFields: FieldOverlay[] = [
    // 4.3 Airway detail (ETT size / cricothyrotomy detail)
    {
      id: "p1-r1care-o2",
      label: "Дих. шляхи",
      page: 1,
      x: 33.43,
      y: 46.6,
      w: 4.83,
      h: 1.7,
      type: "number",
      getValue: (d: any) => d.case.r1Care?.o2,
      command: "case.setR1CareItem",
      getPayload: (v: any, d: any) => ({ uuid: cid(d), key: "o2", value: v }),
    },
    // 4.3 ETT tube size
    {
      id: "p1-r1care-ettSize",
      label: "ETT см",
      page: 1,
      x: 33.98,
      y: 43.83,
      w: 2.3,
      h: 1.2,
      type: "number",
      getValue: (d: any) => d.case.r1Care?.ettSize,
      command: "case.setR1CareItem",
      getPayload: (v: any, d: any) => ({
        uuid: cid(d),
        key: "ettSize",
        value: v,
      }),
    },
    // 4.4 AT/VT tourniquet times
    {
      id: "p1-r1care-tourniquetATTimes1",
      label: "АТ/ВТ час 1",
      page: 1,
      x: 10.13,
      y: 48.89,
      w: 8.996,
      h: 1.2,
      type: "text",
      getValue: (d: any) => d.case.r1Care?.tourniquetATTimes1,
      command: "case.setR1CareItem",
      getPayload: (v: any, d: any) => ({
        uuid: cid(d),
        key: "tourniquetATTimes1",
        value: v,
      }),
    },
    // 4.4 AT/VT tourniquet location
    {
      id: "p1-r1care-tourniquetDetail",
      label: "Локалізація",
      page: 1,
      x: 20.9,
      y: 48.89,
      w: 7.617,
      h: 1.2,
      type: "text",
      getValue: (d: any) => d.case.r1Care?.tourniquetDetail,
      command: "case.setR1CareItem",
      getPayload: (v: any, d: any) => ({
        uuid: cid(d),
        key: "tourniquetDetail",
        value: v,
      }),
    },
    // 4.7 Thoraco blood volume
    {
      id: "p1-r1care-thoracoRightBloodMl",
      label: "торакоцентез П мл",
      page: 1,
      x: 36.32,
      y: 48.99,
      w: 2.41,
      h: 1.2,
      type: "number",
      getValue: (d: any) => d.case.r1Care?.thoracoRightBloodMl,
      command: "case.setR1CareItem",
      getPayload: (v: any, d: any) => ({
        uuid: cid(d),
        key: "thoracoRightBloodMl",
        value: v,
      }),
    },
    {
      id: "p1-r1care-thoracoLeftBloodMl",
      label: "торакоцентез Л мл",
      page: 1,
      x: 36.32,
      y: 50.28,
      w: 2.41,
      h: 1.2,
      type: "number",
      getValue: (d: any) => d.case.r1Care?.thoracoLeftBloodMl,
      command: "case.setR1CareItem",
      getPayload: (v: any, d: any) => ({
        uuid: cid(d),
        key: "thoracoLeftBloodMl",
        value: v,
      }),
    },
    // 4.6 Hypothermia device/method
    {
      id: "p1-r1care-hypothermiaDetail",
      label: "Засіб",
      page: 1,
      x: 59.98,
      y: 47.9,
      w: 14.79,
      h: 1.5,
      type: "text",
      getValue: (d: any) => d.case.r1Care?.hypothermiaDetail,
      command: "case.setR1CareItem",
      getPayload: (v: any, d: any) => ({
        uuid: cid(d),
        key: "hypothermiaDetail",
        value: v,
      }),
    },
    // 4.10 Anesthesia detail
    {
      id: "p1-r1care-anesthesiaDetail",
      label: "Анестезія",
      page: 1,
      x: 76.51,
      y: 51.02,
      w: 19.68,
      h: 2.37,
      type: "textarea",
      textIndent: 8,
      lineHeight: 1.17,
      getValue: (d: any) => d.case.r1Care?.anesthesiaDetail,
      command: "case.setR1CareItem",
      getPayload: (v: any, d: any) => ({
        uuid: cid(d),
        key: "anesthesiaDetail",
        value: v,
      }),
    },
  ];

  return [...checkboxes, ...r1TextFields];
}

// ─── R2 Care checkboxes (Page 2 Section 18) ──────────────────────────

function makeR2CareFields(): FieldOverlay[] {
  const items: Array<{
    key: string;
    x: number;
    y: number;
    labelW?: number;
    label?: string;
  }> = [
    { key: "nfp", x: 37.56, y: 35.02, labelW: 2.75, label: "НФП" },
    { key: "ofp", x: 42.52, y: 35.02, labelW: 2.73, label: "ОФП" },
    { key: "shvl", x: 32.68, y: 35.02, labelW: 2.86, label: "ШВЛ" },
    { key: "ngp", x: 22, y: 36.21, labelW: 2.39, label: "НГП" },
    { key: "ett", x: 26.2, y: 36.21, labelW: 2.3, label: "ETT" },
    {
      key: "cricothyrotomy",
      x: 34.43,
      y: 36.21,
      labelW: 10.04,
      label: "Крікотиреотомія",
    },
    {
      key: "urinaryCatheter",
      x: 49.76,
      y: 35,
      labelW: 10.18,
      label: "Сечовий катетер",
    },
    {
      key: "gastricTube",
      x: 61.67,
      y: 35,
      labelW: 10.05,
      label: "Шлунковий зонд",
    },
    // Eye shields — ck at (85.4, 35.1), (87.8, 35.1)
    { key: "eyeShieldRight", x: 85.36, y: 35.05, labelW: 0.92, label: "П" },
    { key: "eyeShieldLeft", x: 87.76, y: 35.05, labelW: 0.91, label: "Л" },
    // Immobilization — ck at (49.8, 37.8)
    {
      key: "immobilization",
      x: 49.76,
      y: 37.78,
      labelW: 7.95,
      label: "Іммобілізація",
    },
    // Decontamination — ck at (76.3, 36.6)
    {
      key: "decontamination",
      x: 76.32,
      y: 36.64,
      labelW: 9.45,
      label: "Деконтамінація",
    },
    // Occlusive — ck at (34.1, 37.9), (36.6, 37.9)
    { key: "occlusiveRight", x: 34.14, y: 37.92, labelW: 0.92, label: "П" },
    { key: "occlusiveLeft", x: 36.57, y: 37.92, labelW: 0.91, label: "Л" },
    // Needle decomp — ck at (35.5, 38.9), (37.9, 38.9)
    { key: "needleDecompRight", x: 35.48, y: 38.94, labelW: 0.92, label: "П" },
    { key: "needleDecompLeft", x: 37.92, y: 38.94, labelW: 0.91, label: "Л" },
    { key: "pvd", x: 27.08, y: 40.68, labelW: 2.56, label: "ПВД" },
    { key: "cvd", x: 31.68, y: 40.68, labelW: 2.56, label: "ЦВД" },
    { key: "arterial", x: 36.16, y: 40.68, labelW: 8.1, label: "Артеріальна" },
    { key: "ioShoulder", x: 29.41, y: 41.89, labelW: 3.53, label: "в/к плече" },
    { key: "ioTibia", x: 34.44, y: 41.89, labelW: 4.53, label: "в/к г/к" },
    {
      key: "ioSternum",
      x: 40.62,
      y: 41.89,
      labelW: 4.83,
      label: "в/к грудина",
    },
    // Tourniquet limb indicators (R2)
    { key: "tourniquetRUE", x: 3.8, y: 36.2, labelW: 2.75, label: "ПВК" },
    { key: "tourniquetLUE", x: 3.8, y: 37.6, labelW: 3.09, label: "ЛВК" },
    { key: "tourniquetRLE", x: 3.8, y: 38.9, labelW: 3.2, label: "ПНК" },
    { key: "tourniquetLLE", x: 3.8, y: 40.3, labelW: 2.86, label: "ЛНК" },
    // Wound — ck at (3.8, 42.0) / (3.8, 43.1)
    { key: "bandage", x: 3.796, y: 41.98, labelW: 5.21, label: "Пов'язка" },
    { key: "packing", x: 3.796, y: 43.06, labelW: 8.68, label: "Тампонування" },
    // O2
    { key: "o2", x: 22, y: 35.02, labelW: 0.88, label: "O₂" },
    // Immobilization sub-types
    { key: "immobCervical", x: 58.48, y: 36.61, labelW: 2.85, label: "ШВХ" },
    { key: "immobSpine", x: 62.91, y: 36.61, labelW: 4.4, label: "Хребет" },
    { key: "immobPelvis", x: 68.7, y: 36.62, labelW: 2.12, label: "Таз" },
    // PVD limb locations
    { key: "pvdRue", x: 49.76, y: 37.78, labelW: 2.51, label: "ПВК" },
    { key: "pvdLue", x: 53.79, y: 37.76, labelW: 2.51, label: "ЛВК" },
    { key: "pvdRle", x: 57.94, y: 37.76, labelW: 2.62, label: "ПНК" },
    { key: "pvdLle", x: 62.24, y: 37.76, labelW: 2.62, label: "ЛНК" },
    // hypothermiaActive / hypothermiaPassive — moved to radioOpts below
    // Thoracocentesis findings
    {
      key: "thoracoRightBlood",
      x: 73.44,
      y: 40.65,
      labelW: 2.97,
      label: "П Кров",
    },
    {
      key: "thoracoRightAir",
      x: 78.06,
      y: 40.65,
      labelW: 4.6,
      label: "П Повітря",
    },
    {
      key: "thoracoLeftBlood",
      x: 86.11,
      y: 40.65,
      labelW: 2.97,
      label: "Л Кров",
    },
    {
      key: "thoracoLeftAir",
      x: 90.73,
      y: 40.65,
      labelW: 4.6,
      label: "Л Повітря",
    },
  ];

  const checkboxes = items.map((item) =>
    cb(
      `p2-r2care-${item.key}`,
      2,
      item.x,
      item.y,
      "case.setR2CareItem",
      (d: any) => d.case.r2Care?.[item.key],
      (v: any, d: any) => ({
        uuid: cid(d),
        key: item.key,
        value: !!v,
      }),
      item.labelW,
      item.label,
    ),
  );

  const r2TextFields: FieldOverlay[] = [
    {
      id: "p2-r2care-o2Flow",
      label: "O₂ л/хв",
      page: 2,
      x: 25.5,
      y: 35.6,
      w: 3.8,
      h: 1.2,
      type: "number",
      getValue: (d: any) => d.case.r2Care?.o2Flow,
      command: "case.setR2CareItem",
      getPayload: (v: any, d: any) => ({
        uuid: cid(d),
        key: "o2Flow",
        value: v,
      }),
    },
    {
      id: "p2-r2care-ettSize",
      label: "ETT см",
      page: 2,
      x: 29.8,
      y: 36.8,
      w: 2.3,
      h: 1.2,
      type: "number",
      getValue: (d: any) => d.case.r2Care?.ettSize,
      command: "case.setR2CareItem",
      getPayload: (v: any, d: any) => ({
        uuid: cid(d),
        key: "ettSize",
        value: v,
      }),
    },
  ];

  // Hypothermia active/passive — independent checkboxes (both can be selected)
  const hypothermiaCheckboxes: FieldOverlay[] = [
    cb(
      "p2-r2care-hypothermiaActive", 2, 37.71, 43.56,
      "case.setR2CareItem",
      (d: any) => d.case.r2Care?.hypothermiaActive,
      (v: any, d: any) => ({ uuid: cid(d), key: "hypothermiaActive", value: !!v }),
      0.85, "А",
    ),
    cb(
      "p2-r2care-hypothermiaPassive", 2, 40.27, 43.56,
      "case.setR2CareItem",
      (d: any) => d.case.r2Care?.hypothermiaPassive,
      (v: any, d: any) => ({ uuid: cid(d), key: "hypothermiaPassive", value: !!v }),
      0.85, "П",
    ),
  ];

  // Hypothermia notes text field
  const hypothermiaDetail: FieldOverlay = {
    id: "p2-r2care-hypothermiaDetail",
    label: "Засіб",
    page: 2,
    x: 25.1,
    y: 45.2,
    w: 21.56,
    h: 1,
    type: "text",
    editAs: "textarea",
    getValue: (d: any) => d.case.r2Care?.hypothermiaDetail,
    command: "case.setR2CareItem",
    getPayload: (v: any, d: any) => ({
      uuid: cid(d),
      key: "hypothermiaDetail",
      value: v,
    }),
  };

  return [...checkboxes, ...r2TextFields, ...hypothermiaCheckboxes, hypothermiaDetail];
}

// ─── Diagnostics fields ─────────────────────────────────────────────

function makeDiagnosticsFields(page: 1 | 2): FieldOverlay[] {
  const role = page as 1 | 2;
  const ac = {
    command: "diagnostics.create",
    getPayload: (caseId: string) => ({ caseUuid: caseId, role }),
  };
  const getRow = (d: any) =>
    (d.diagnostics || []).find((r: any) => (r.role ?? 1) === role) || null;
  const getUuid = (d: any) => getRow(d)?.id;

  if (page === 1) {
    return [
      ...radioOpts(
        "p1-diag-efast",
        1,
        "diagnostics.setEFast",
        (d) => getRow(d)?.eFast,
        (v, d, n) => ({ uuid: n || getUuid(d), value: v }),
        [
          { value: "+", x: 9.622, y: 65.83, labelW: 1.01 },
          { value: "-", x: 12.77, y: 65.83, labelW: 0.77 },
          { value: "+/-", x: 15.47, y: 65.83, labelW: 1.98 },
        ],
        ac,
      ),
      // Checkboxes for Ro, Отоскопія П/Л, ЕКГ — checked = truthy text value
      {
        id: "p1-diag-xray-cb",
        label: "Ro",
        page: 1,
        x: 25.53,
        y: 65.83,
        w: CW,
        h: CH,
        type: "checkbox",
        labelW: 1.78,
        getValue: (d) => !!getRow(d)?.xray,
        command: "diagnostics.setXray",
        getPayload: (v, d, n) => ({
          uuid: n || getUuid(d),
          value: v ? getRow(d)?.xray || "+" : "",
        }),
        autoCreate: ac,
      },
      {
        id: "p1-diag-otoscopy-r-cb",
        label: "П",
        page: 1,
        x: 56.66,
        y: 65.83,
        w: CW,
        h: CH,
        type: "checkbox",
        labelW: 1.27,
        getValue: (d) => !!getRow(d)?.otoscopyRight,
        command: "diagnostics.setOtoscopyRight",
        getPayload: (v, d, n) => ({
          uuid: n || getUuid(d),
          value: v ? getRow(d)?.otoscopyRight || "+" : "",
        }),
        autoCreate: ac,
      },
      {
        id: "p1-diag-otoscopy-l-cb",
        label: "Л",
        page: 1,
        x: 71.48,
        y: 65.83,
        w: CW,
        h: CH,
        type: "checkbox",
        labelW: 1.55,
        getValue: (d) => !!getRow(d)?.otoscopyLeft,
        command: "diagnostics.setOtoscopyLeft",
        getPayload: (v, d, n) => ({
          uuid: n || getUuid(d),
          value: v ? getRow(d)?.otoscopyLeft || "+" : "",
        }),
        autoCreate: ac,
      },
      {
        id: "p1-diag-ecg-cb",
        label: "ЕКГ",
        page: 1,
        x: 25.53,
        y: 67.17,
        w: CW,
        h: CH,
        type: "checkbox",
        labelW: 2.4,
        getValue: (d) => !!getRow(d)?.ecg,
        command: "diagnostics.setEcg",
        getPayload: (v, d, n) => ({
          uuid: n || getUuid(d),
          value: v ? getRow(d)?.ecg || "+" : "",
        }),
        autoCreate: ac,
      },
      // Text fields
      {
        id: "p1-diag-xray",
        label: "Ro",
        page: 1,
        x: 29.3,
        y: 66.59,
        w: 19.1,
        h: 1.3,
        type: "text",
        getValue: (d) => getRow(d)?.xray,
        command: "diagnostics.setXray",
        getPayload: (v, d, n) => ({ uuid: n || getUuid(d), value: v }),
        autoCreate: ac,
      },
      {
        id: "p1-diag-otoscopy-r",
        label: "Отоскопія П",
        page: 1,
        x: 58.8,
        y: 66.59,
        w: 11.4,
        h: 1.3,
        type: "text",
        getValue: (d) => getRow(d)?.otoscopyRight,
        command: "diagnostics.setOtoscopyRight",
        getPayload: (v, d, n) => ({ uuid: n || getUuid(d), value: v }),
        autoCreate: ac,
      },
      {
        id: "p1-diag-otoscopy-l",
        label: "Отоскопія Л",
        page: 1,
        x: 73.9,
        y: 66.59,
        w: 11.3,
        h: 1.3,
        type: "text",
        getValue: (d) => getRow(d)?.otoscopyLeft,
        command: "diagnostics.setOtoscopyLeft",
        getPayload: (v, d, n) => ({ uuid: n || getUuid(d), value: v }),
        autoCreate: ac,
      },
      {
        id: "p1-diag-pupils",
        label: "Зіниці",
        page: 1,
        x: 92,
        y: 66.6,
        w: 1.4,
        h: 2,
        type: "select",
        getValue: (d) => getRow(d)?.pupilsLeft,
        command: "diagnostics.setPupilsLeft",
        getPayload: (v, d, n) => ({ uuid: n || getUuid(d), value: v }),
        options: [
          { value: "<", label: "<" },
          { value: "=", label: "=" },
          { value: ">", label: ">" },
        ],
        autoCreate: ac,
      },
      {
        id: "p1-diag-ecg",
        label: "ЕКГ",
        page: 1,
        x: 29.3,
        y: 67.93,
        w: 19.1,
        h: 1.3,
        type: "text",
        getValue: (d) => getRow(d)?.ecg,
        command: "diagnostics.setEcg",
        getPayload: (v, d, n) => ({ uuid: n || getUuid(d), value: v }),
        autoCreate: ac,
      },
      // "Л." notes field
      {
        id: "p1-diag-notes",
        label: "Локалізація",
        page: 1,
        x: 5.5,
        y: 67.93,
        w: 18.52,
        h: 1.3,
        type: "text",
        getValue: (d) => getRow(d)?.notes,
        command: "diagnostics.setNotes",
        getPayload: (v, d, n) => ({ uuid: n || getUuid(d), value: v }),
        autoCreate: ac,
      },
      // "Інше" other diagnostics field
      {
        id: "p1-diag-other",
        label: "Інше",
        page: 1,
        x: 53,
        y: 67.93,
        w: 42.96,
        h: 1.3,
        type: "text",
        getValue: (d) => getRow(d)?.other,
        command: "diagnostics.setOther",
        getPayload: (v, d, n) => ({ uuid: n || getUuid(d), value: v }),
        autoCreate: ac,
      },
    ];
  } else {
    // P2 section 17 — Діагностика
    // Row 1: E-FAST (+/-/+/-) | Ro □ КТ □ ЕКГ □ УЗД □ | Lab values (separate fn)
    // Row 2: "Л." notes field  | Отоскопія П ___ Л ___
    return [
      // E-FAST radio checkboxes
      ...radioOpts(
        "p2-diag-efast",
        2,
        "diagnostics.setEFast",
        (d) => getRow(d)?.eFast,
        (v, d, n) => ({ uuid: n || getUuid(d), value: v }),
        [
          { value: "+", x: 9.62, y: 28.61, labelW: 1.02 },
          { value: "-", x: 12.77, y: 28.61, labelW: 0.76 },
          { value: "+/-", x: 15.47, y: 28.61, labelW: 1.98 },
        ],
        ac,
      ),
      {
        id: "p2-diag-xray",
        label: "Ro",
        page: 2,
        x: 23.3,
        y: 28.61,
        w: CW,
        h: CH,
        type: "checkbox",
        labelW: 1.78,
        getValue: (d) => getRow(d)?.xray,
        command: "diagnostics.setXray",
        getPayload: (v: any, d: any, n?: string) => ({
          uuid: n || getUuid(d),
          value: v ? "true" : "",
        }),
        autoCreate: ac,
      },
      {
        id: "p2-diag-ct",
        label: "КТ",
        page: 2,
        x: 26.98,
        y: 28.61,
        w: CW,
        h: CH,
        type: "checkbox",
        labelW: 1.79,
        getValue: (d) => getRow(d)?.ct,
        command: "diagnostics.setCt",
        getPayload: (v: any, d: any, n?: string) => ({
          uuid: n || getUuid(d),
          value: v ? "true" : "",
        }),
        autoCreate: ac,
      },
      {
        id: "p2-diag-ecg",
        label: "ЕКГ",
        page: 2,
        x: 30.67,
        y: 28.61,
        w: CW,
        h: CH,
        type: "checkbox",
        labelW: 2.42,
        getValue: (d) => getRow(d)?.ecg,
        command: "diagnostics.setEcg",
        getPayload: (v: any, d: any, n?: string) => ({
          uuid: n || getUuid(d),
          value: v ? "true" : "",
        }),
        autoCreate: ac,
      },
      {
        id: "p2-diag-us",
        label: "УЗД",
        page: 2,
        x: 34.99,
        y: 28.61,
        w: CW,
        h: CH,
        type: "checkbox",
        labelW: 2.58,
        getValue: (d) => getRow(d)?.ultrasound,
        command: "diagnostics.setUltrasound",
        getPayload: (v: any, d: any, n?: string) => ({
          uuid: n || getUuid(d),
          value: v ? "true" : "",
        }),
        autoCreate: ac,
      },
      {
        id: "p2-diag-otoscopy-r-cb",
        label: "П",
        page: 2,
        x: 29.9,
        y: 30,
        w: CW,
        h: CH,
        type: "checkbox",
        labelW: 0.92,
        getValue: (d) => !!getRow(d)?.otoscopyRight,
        command: "diagnostics.setOtoscopyRight",
        getPayload: (v: any, d: any, n?: string) => ({
          uuid: n || getUuid(d),
          value: v ? getRow(d)?.otoscopyRight || "+" : "",
        }),
        autoCreate: ac,
      },
      {
        id: "p2-diag-otoscopy-l-cb",
        label: "Л",
        page: 2,
        x: 36.6,
        y: 30,
        w: CW,
        h: CH,
        type: "checkbox",
        labelW: 0.91,
        getValue: (d) => !!getRow(d)?.otoscopyLeft,
        command: "diagnostics.setOtoscopyLeft",
        getPayload: (v: any, d: any, n?: string) => ({
          uuid: n || getUuid(d),
          value: v ? getRow(d)?.otoscopyLeft || "+" : "",
        }),
        autoCreate: ac,
      },
      {
        id: "p2-diag-otoscopy-r",
        label: "Отоскопія П",
        page: 2,
        x: 32.06,
        y: 30.71,
        w: 3.81,
        h: 1.2,
        type: "text",
        getValue: (d) => getRow(d)?.otoscopyRight,
        command: "diagnostics.setOtoscopyRight",
        getPayload: (v, d, n) => ({ uuid: n || getUuid(d), value: v }),
        autoCreate: ac,
      },
      {
        id: "p2-diag-otoscopy-l",
        label: "Отоскопія Л",
        page: 2,
        x: 38.91,
        y: 30.71,
        w: 3.81,
        h: 1.2,
        type: "text",
        getValue: (d) => getRow(d)?.otoscopyLeft,
        command: "diagnostics.setOtoscopyLeft",
        getPayload: (v, d, n) => ({ uuid: n || getUuid(d), value: v }),
        autoCreate: ac,
      },
      {
        id: "p2-diag-notes",
        label: "Локалізація",
        page: 2,
        x: 5.16,
        y: 30.71,
        w: 16.78,
        h: 1.2,
        type: "text",
        getValue: (d) => getRow(d)?.notes,
        command: "diagnostics.setNotes",
        getPayload: (v, d, n) => ({ uuid: n || getUuid(d), value: v }),
        autoCreate: ac,
      },
    ];
  }
}

// ─── Lab Values (Page 2) ────────────────────────────────────────────

function makeLabValuesFields(): FieldOverlay[] {
  const ac = {
    command: "labValues.create",
    getPayload: (caseId: string) => ({ caseUuid: caseId, role: 2 }),
  };
  const getRow = (d: any) =>
    (d.labValues || []).find((r: any) => (r.role ?? 2) === 2) || null;
  const getUuid = (d: any) => getRow(d)?.id;

  const row1: Array<{
    id: string;
    x: number;
    w: number;
    field: string;
    cmd: string;
    label: string;
  }> = [
    {
      id: "hg",
      x: 46,
      w: 2.4,
      field: "hg",
      cmd: "labValues.setHg",
      label: "Hg",
    },
    {
      id: "hct",
      x: 51.6,
      w: 2.4,
      field: "hct",
      cmd: "labValues.setHct",
      label: "Hct",
    },
    {
      id: "rbc",
      x: 56.9,
      w: 2.4,
      field: "rbc",
      cmd: "labValues.setRbc",
      label: "Ер",
    },
    {
      id: "wbc",
      x: 61.3,
      w: 2.4,
      field: "wbc",
      cmd: "labValues.setWbc",
      label: "Л",
    },
    {
      id: "plt",
      x: 66.4,
      w: 2.4,
      field: "platelets",
      cmd: "labValues.setPlatelets",
      label: "Тр",
    },
    {
      id: "ph",
      x: 73.3,
      w: 2.9,
      field: "ph",
      cmd: "labValues.setPh",
      label: "pH",
    },
    {
      id: "pco2",
      x: 80,
      w: 2.9,
      field: "pCO2",
      cmd: "labValues.setPCO2",
      label: "pCO₂",
    },
    {
      id: "po2",
      x: 86.1,
      w: 2.9,
      field: "pO2",
      cmd: "labValues.setPO2",
      label: "pO₂",
    },
    {
      id: "hco3",
      x: 93.3,
      w: 2.9,
      field: "hCO3",
      cmd: "labValues.setHCO3",
      label: "HCO₃",
    },
  ];
  const row2: Array<{
    id: string;
    x: number;
    w: number;
    field: string;
    cmd: string;
    label: string;
  }> = [
    {
      id: "na",
      x: 46,
      w: 2.4,
      field: "na",
      cmd: "labValues.setNa",
      label: "Na",
    },
    { id: "k", x: 50.2, w: 2.4, field: "k", cmd: "labValues.setK", label: "K" },
    {
      id: "ca",
      x: 55,
      w: 2.4,
      field: "ca",
      cmd: "labValues.setCa",
      label: "Ca",
    },
    {
      id: "cl",
      x: 59.4,
      w: 2.4,
      field: "cl",
      cmd: "labValues.setCl",
      label: "Cl",
    },
    {
      id: "lactate",
      x: 67.8,
      w: 2.4,
      field: "lactate",
      cmd: "labValues.setLactate",
      label: "Лактат",
    },
    {
      id: "urea",
      x: 76.6,
      w: 2.4,
      field: "urea",
      cmd: "labValues.setUrea",
      label: "Сечовина",
    },
    {
      id: "creatinine",
      x: 85.9,
      w: 2.4,
      field: "creatinine",
      cmd: "labValues.setCreatinine",
      label: "Креатинін",
    },
    {
      id: "glucose",
      x: 93.8,
      w: 2.4,
      field: "glucose",
      cmd: "labValues.setGlucose",
      label: "Глюкоза",
    },
  ];

  const fields: FieldOverlay[] = [];
  for (const item of row1) {
    fields.push({
      id: `p2-lab-${item.id}`,
      label: item.label,
      page: 2,
      x: item.x,
      y: 29.37,
      w: item.w,
      h: 1.3,
      type: "number",
      getValue: (d) => getRow(d)?.[item.field],
      command: item.cmd,
      getPayload: (v, d, n) => ({ uuid: n || getUuid(d), value: v }),
      autoCreate: ac,
    });
  }
  for (const item of row2) {
    fields.push({
      id: `p2-lab-${item.id}`,
      label: item.label,
      page: 2,
      x: item.x,
      y: 30.73,
      w: item.w,
      h: 1.3,
      type: "number",
      getValue: (d) => getRow(d)?.[item.field],
      command: item.cmd,
      getPayload: (v, d, n) => ({ uuid: n || getUuid(d), value: v }),
      autoCreate: ac,
    });
  }
  fields.push({
    id: "p2-lab-notes",
    label: "Лаб.",
    page: 2,
    x: 21.17,
    y: 32.33,
    w: 74.98,
    h: 1.2,
    type: "text",
    getValue: (d) => getRow(d)?.notes,
    command: "labValues.setNotes",
    getPayload: (v, d, n) => ({ uuid: n || getUuid(d), value: v }),
    autoCreate: ac,
  });
  return fields;
}

// ─── Surgery fields (Page 2) ────────────────────────────────────────
// "Оперативне втручання" at (5.8, 46.2) — textarea for description
// 18.14 "Вид анестезії" at (5.8, 53.8): Локальна(16) Регіонарна(23.7) Загальна(32.4) Седація(39.7)
// 18.15 Surgery start date(19.3, 56.4) time colon(40.9, 56.3); end date(67.4) time colon(88.9)

function makeSurgeryFields(): FieldOverlay[] {
  const ac = {
    command: "surgery.create",
    getPayload: (caseId: string) => ({ caseUuid: caseId, role: 2 }),
  };
  const getRow = (d: any) =>
    (d.surgeries || []).find((r: any) => (r.role ?? 2) === 2) || null;
  const getUuid = (d: any) => getRow(d)?.id;

  return [
    // Description textarea
    {
      id: "p2-surgery-desc",
      label: "Оперативне втручання",
      page: 2,
      x: 3.8,
      y: 51.28,
      w: 92.42,
      h: 6.24,
      type: "textarea",
      textIndent: 15.8,
      lineHeight: 1.247,
      getValue: (d) => getRow(d)?.description,
      command: "surgery.setDescription",
      getPayload: (v, d, n) => ({ uuid: n || getUuid(d), description: v }),
      autoCreate: ac,
    },
    ...(
      [
        { value: "local", col: "anesthesiaLocal", label: "Локальна", x: 14.91, y: 54.02, labelW: 5.87 },
        { value: "regional", col: "anesthesiaRegional", label: "Регіонарна", x: 22.62, y: 54.02, labelW: 6.72 },
        { value: "general", col: "anesthesiaGeneral", label: "Загальна", x: 31.24, y: 54.02, labelW: 5.42 },
        { value: "sedation", col: "anesthesiaSedation", label: "Седація", x: 38.63, y: 54.02, labelW: 4.7 },
      ] as const
    ).map((o): FieldOverlay => ({
      id: `p2-surgery-anesthesia-${o.value}`,
      label: o.label,
      page: 2,
      x: o.x,
      y: o.y,
      w: CW,
      h: CH,
      type: "checkbox",
      labelW: o.labelW,
      getValue: (d) => !!getRow(d)?.[o.col],
      command: "surgery.setAnesthesiaTypeItem",
      getPayload: (checked, d, n) => ({
        uuid: n || getUuid(d),
        key: o.value,
        value: !!checked,
      }),
      autoCreate: ac,
    })),
    // 18.14 anesthesia notes textarea (2 SVG underlines at y=460.17 and y=471.68 → 54.66% and 56.03%)
    {
      id: "p2-surgery-anesthesia-notes",
      label: "Анестезія",
      page: 2,
      x: 3.994,
      y: 55.689,
      w: 92.42,
      h: 2.74,
      type: "textarea",
      textIndent: 40.5,
      lineHeight: 1.37,
      getValue: (d: any) => d.case.r2Care?.anesthesiaNotes,
      command: "case.setR2CareItem",
      getPayload: (v: any, d: any) => ({
        uuid: cid(d),
        key: "anesthesiaNotes",
        value: v,
      }),
    },
    {
      id: "p2-surgery-start-date",
      label: "Дата початку",
      page: 2,
      x: 23,
      y: 57.36,
      w: 10.9,
      h: 1.2,
      type: "datetime-date",
      getValue: (d) => getRow(d)?.startTime,
      command: "surgery.setStartTime",
      getPayload: (v, d, n) => ({ uuid: n || getUuid(d), timestamp: v }),
      autoCreate: ac,
    },
    {
      id: "p2-surgery-start",
      label: "Час початку",
      page: 2,
      x: 37.9,
      y: 57.36,
      w: 6.3,
      h: 1.2,
      type: "time",
      getValue: (d) => getRow(d)?.startTime,
      command: "surgery.setStartTime",
      getPayload: (v, d, n) => ({ uuid: n || getUuid(d), timestamp: v }),
      autoCreate: ac,
    },
    {
      id: "p2-surgery-end-date",
      label: "Дата кінця",
      page: 2,
      x: 71.1,
      y: 57.36,
      w: 10.9,
      h: 1.2,
      type: "datetime-date",
      getValue: (d) => getRow(d)?.endTime,
      command: "surgery.setEndTime",
      getPayload: (v, d, n) => ({ uuid: n || getUuid(d), timestamp: v }),
      autoCreate: ac,
    },
    {
      id: "p2-surgery-end",
      label: "Час кінця",
      page: 2,
      x: 86,
      y: 57.36,
      w: 6.3,
      h: 1.2,
      type: "time",
      getValue: (d) => getRow(d)?.endTime,
      command: "surgery.setEndTime",
      getPayload: (v, d, n) => ({ uuid: n || getUuid(d), timestamp: v }),
      autoCreate: ac,
    },
  ];
}

// ─── Page 2 Patient Exam (Section 16) ────────────────────────────────
// Extensive checkbox/radio section covering airway, breathing, skin,
// circulation, head, chest, abdomen, limbs, eyes.

function makeExamFields(): FieldOverlay[] {
  const ac = {
    command: "exam.create",
    getPayload: (caseId: string) => ({ caseUuid: caseId, role: 2 }),
  };
  const getRow = (d: any) =>
    (d.exam || []).find((r: any) => (r.role ?? 2) === 2) || null;
  const getUuid = (d: any) => getRow(d)?.id;

  /** Like radioOpts but without exclusive radio behaviour — each checkbox is independent. */
  const examOpts = (
    idPrefix: string,
    command: string,
    getValue: (d: any) => any,
    mkPayload: (
      val: string | null,
      d: any,
      newUuid?: string,
    ) => Record<string, unknown> | null,
    opts: Array<{
      value: string;
      label?: string;
      x: number;
      y: number;
      labelW?: number;
      labelRect?: { x: number; y: number; w: number; h: number };
    }>,
  ): FieldOverlay[] =>
    opts.map((o) => {
      const lw = o.labelW ?? 5;
      const rl = o.label ?? o.value;
      return {
        id: `${idPrefix}-${o.value}`,
        label: rl,
        page: 2 as const,
        x: o.x,
        y: o.y,
        w: CW,
        h: CH,
        type: "checkbox" as const,
        labelW: lw,
        labelRect: o.labelRect ?? { x: o.x, y: o.y, w: CW + lw, h: CH },
        getValue: (d: any) => {
          const v = getValue(d);
          return v != null && String(v) === o.value;
        },
        command,
        getPayload: (checked: any, d: any, newUuid?: string) =>
          mkPayload(checked ? o.value : null, d, newUuid),
        autoCreate: ac,
      };
    });

  const ecb = (
    id: string,
    x: number,
    y: number,
    field: string,
    command: string,
    labelW = 7,
    label?: string,
  ): FieldOverlay => ({
    id,
    page: 2,
    x,
    y,
    w: CW,
    h: CH,
    type: "checkbox",
    labelW,
    ...(label ? { label } : {}),
    getValue: (d) => getRow(d)?.[field],
    command,
    getPayload: (v, d, n) => ({ uuid: n || getUuid(d), value: v }),
    autoCreate: ac,
  });

  /** Independent boolean checkbox pair — each toggles between its boolean value and null. */
  const boolOpts = (
    idPrefix: string,
    field: string,
    command: string,
    opts: Array<{
      boolVal: boolean;
      label: string;
      x: number;
      y: number;
      labelW?: number;
      labelRect?: { x: number; y: number; w: number; h: number };
    }>,
  ): FieldOverlay[] =>
    opts.map((o) => ({
      id: `${idPrefix}-${o.boolVal ? "1" : "0"}`,
      label: o.label,
      page: 2 as const,
      x: o.x,
      y: o.y,
      w: CW,
      h: CH,
      type: "checkbox" as const,
      labelW: o.labelW ?? 5,
      ...(o.labelRect ? { labelRect: o.labelRect } : {}),
      getValue: (d: any) => {
        const v = getRow(d)?.[field];
        return v === o.boolVal || v === (o.boolVal ? 1 : 0);
      },
      command,
      getPayload: (_checked: any, d: any, n?: string) => ({
        uuid: n || getUuid(d),
        value: _checked ? o.boolVal : null,
      }),
      autoCreate: ac,
    }));

  return [
    // ── A: Airway Status ──
    ...examOpts(
      "p2-airway",
      "exam.setAirwayStatus",
      (d) => getRow(d)?.airwayStatus,
      (v, d, n) => ({ uuid: n || getUuid(d), airwayStatus: v }),
      [
        {
          value: "patent",
          label: "Прохідні",
          x: 14.64,
          y: 9.656,
          labelRect: { x: 15.65, y: 9.56, w: 7, h: 0.85 },
        },
        {
          value: "partialObstruction",
          label: "Часткова",
          x: 12.07,
          y: 10.89,
          labelW: 5.6,
        },
        {
          value: "fullObstruction",
          label: "Повна",
          x: 19.57,
          y: 10.82,
          labelW: 5,
        },
      ],
    ),
    // ЗПДШ наявний (advanced airway present) — checkbox at far left
    {
      id: "p2-exam-airway-present",
      label: "ЗПДШ наявний",
      page: 2,
      x: 3.864,
      y: 12.05,
      w: CW,
      h: CH,
      type: "checkbox",
      labelW: 7.42,
      getValue: (d) => getRow(d)?.breathingSounds?.advancedAirwayPresent,
      command: "exam.setBreathingSoundsItem",
      getPayload: (v, d, n) => {
        const uuid = n || getUuid(d);
        if (!uuid) return null;
        return { uuid, key: "advancedAirwayPresent", value: !!v };
      },
      autoCreate: ac,
    },
    {
      id: "p2-exam-airway-type",
      label: "ЗПДШ тип",
      page: 2,
      x: 12.3,
      y: 12.7,
      w: 13,
      h: 1.2,
      type: "text",
      getValue: (d) => getRow(d)?.breathingSounds?.advancedAirwayTypeDetail,
      command: "exam.setBreathingSoundsItem",
      getPayload: (v, d, n) => ({
        uuid: n || getUuid(d),
        key: "advancedAirwayTypeDetail",
        value: v,
      }),
      autoCreate: ac,
    },
    // ЗПДШ device type checkboxes (ETT, НФП, ОФП)
    ...examOpts(
      "p2-airway-device",
      "exam.setAdvancedAirwayType",
      (d) => getRow(d)?.advancedAirwayType,
      (v, d, n) => ({ uuid: n || getUuid(d), value: v }),
      [
        { value: "ETT", x: 10.04, y: 13.51, labelW: 4.11 },
        { value: "LMA", x: 15.95, y: 13.51, labelW: 5.71 },
        { value: "NPA", x: 19.57, y: 10.82, labelW: 3.88 },
      ],
    ),

    // ── B: Breathing sounds ──
    // Respiratory support checkbox — left margin
    {
      id: "p2-exam-resp-support",
      label: "Респ. підтримка",
      page: 2,
      x: 3.864,
      y: 14.69,
      w: CW,
      h: CH,
      type: "checkbox",
      labelW: 6.38,
      getValue: (d) => getRow(d)?.breathingSounds?.respiratorySupport,
      command: "exam.setBreathingSoundsItem",
      getPayload: (v, d, n) => {
        const uuid = n || getUuid(d);
        if (!uuid) return null;
        return { uuid, key: "respiratorySupport", value: !!v };
      },
      autoCreate: ac,
    },
    {
      id: "p2-exam-resp-support-type",
      label: "Порушене",
      page: 2,
      x: 11.4,
      y: 15.3,
      w: 14.2,
      h: 1.1,
      type: "text",
      getValue: (d) => getRow(d)?.respiratorySupportType,
      command: "exam.setRespiratorySupportType",
      getPayload: (v, d, n) => ({ uuid: n || getUuid(d), value: v }),
      autoCreate: ac,
    },
    // Breathing sounds ──
    // Uses JSON field breathingSounds: Record<string,boolean>
    //            R4 y=176.49(20.96%), R5 y=186.61(22.17%)
    ...(
      [
        "vesicularR",
        20.2997,
        17.3586,
        "vesicularL",
        22.8632,
        17.3586,
        "wetRalesR",
        12.4177,
        18.5606,
        "wetRalesL",
        14.9812,
        18.5606,
        "dryRalesR",
        10.8638,
        19.7627,
        "dryRalesL",
        13.2593,
        19.7627,
        "diminishedR",
        10.8638,
        20.9635,
        "diminishedL",
        13.4273,
        20.9635,
        "absentR",
        9.5098,
        22.1656,
        "absentL",
        12.075,
        22.1656,
      ] as const
    ).reduce<FieldOverlay[]>((arr, _, i, src) => {
      if (i % 3 !== 0) return arr;
      const key = src[i] as string,
        x = src[i + 1] as number,
        y = src[i + 2] as number;
      const bsLabel = key.endsWith("R") ? "П" : "Л";
      arr.push({
        id: `p2-exam-bs-${key}`,
        label: bsLabel,
        page: 2,
        x,
        y,
        w: CW,
        h: CH,
        type: "checkbox",
        labelW: 1.11,
        getValue: (d) => getRow(d)?.breathingSounds?.[key],
        command: "exam.setBreathingSoundsItem",
        getPayload: (v, d, n) => {
          const uuid = n || getUuid(d);
          if (!uuid) return null;
          return { uuid, key, value: !!v };
        },
        autoCreate: ac,
      });
      return arr;
    }, []),

    // ── C: Skin ──
    ...examOpts(
      "p2-skin-temp",
      "exam.setSkinTemperature",
      (d) => getRow(d)?.skinTemperature,
      (v, d, n) => ({ uuid: n || getUuid(d), value: v }),
      [
        { value: "warm", label: "Тепла", x: 32.16, y: 9.656, labelW: 3.64 },
        { value: "cold", label: "Холодна", x: 37.44, y: 9.656, labelW: 5.22 },
      ],
    ),
    ecb("p2-skin-pink", 27.47, 10.86, "skinPink", "exam.setSkinPink", 4.64, "Рожева"),
    ecb("p2-skin-pale", 33.96, 10.86, "skinPale", "exam.setSkinPale", 3.41, "Бліда"),
    ecb("p2-skin-cyanotic", 39.17, 10.86, "skinCyanotic", "exam.setSkinCyanotic", 6.9, "Ціанотична"),
    ecb("p2-skin-dry", 27.47, 12.06, "skinDry", "exam.setSkinDry", 1.12, "Суха"),
    ecb("p2-skin-moist", 32.08, 12.07, "skinMoist", "exam.setSkinMoist", 4.33, "Волога"),
    ecb("p2-exam-rash", 27.4677, 13.269, "skinRash", "exam.setSkinRash", 5.18, "Висип"),
    ecb("p2-exam-edema", 34.1604, 13.269, "skinEdema", "exam.setSkinEdema", 5.23, "Набряк"),

    // ── C: Peripheral pulse ──
    ...examOpts(
      "p2-pulse",
      "exam.setPeripheralPulse",
      (d) => getRow(d)?.peripheralPulse,
      (v, d, n) => ({ uuid: n || getUuid(d), value: v }),
      [
        {
          value: "present",
          label: "Наявний",
          x: 27.47,
          y: 15.92,
          labelW: 5.29,
        },
        { value: "weak", label: "Слабкий", x: 34.45, y: 15.92, labelW: 5.22 },
        {
          value: "absent",
          label: "Відсутній",
          x: 27.47,
          y: 17.12,
          labelW: 5.76,
        },
      ],
    ),

    // ── C: Capillary refill ──
    ...boolOpts("p2-cap-refill", "capillaryRefillOver2s", "exam.setCapillaryRefillOver2s", [
      { boolVal: false, label: "<2с", x: 27.47, y: 19.65, labelW: 1.89 },
      { boolVal: true, label: ">2с", x: 31.16, y: 19.65, labelW: 2.87 },
    ]),

    // ── C: Heart sounds ──
    ...boolOpts("p2-heart", "heartSoundsClear", "exam.setHeartSoundsClear", [
      { boolVal: true, label: "Чисті", x: 27.47, y: 22.17, labelW: 2.73 },
      { boolVal: false, label: "Приглушені", x: 31.76, y: 22.17, labelW: 7.29 },
    ]),

    // ── D: Head ──
    ...boolOpts("p2-skull", "skullFracture", "exam.setSkullFracture", [
      { boolVal: false, label: "Ні", x: 48.98, y: 10.74, labelW: 1.46 },
      { boolVal: true, label: "Так", x: 51.83, y: 10.74, labelW: 2.22 },
    ]),
    ...boolOpts("p2-csf", "csfLeak", "exam.setCsfLeak", [
      {
        boolVal: false,
        label: "Ні",
        x: 61.69,
        y: 10.74,
        labelW: 3.6,
        labelRect: { x: 61.69, y: 10.74, w: 2.419, h: 0.6141 },
      },
      {
        boolVal: true,
        label: "Так",
        x: 64.37,
        y: 10.74,
        labelW: 4.53,
        labelRect: { x: 64.37, y: 10.74, w: 3.111, h: 0.6141 },
      },
    ]),
    ...boolOpts("p2-c-spine", "cervicalSpineInjury", "exam.setCervicalSpineInjury", [
      { boolVal: false, label: "Ні", x: 63.84, y: 12.2, labelW: 2 },
      { boolVal: true, label: "Так", x: 66.69, y: 12.2, labelW: 2 },
    ]),
    ecb("p2-trachea-dev-r", 66.3, 14.62, "tracheaDeviationRight", "exam.setTracheaDeviationRight", 1.11, "П"),
    ecb("p2-trachea-dev-l", 68.84, 14.62, "tracheaDeviationLeft", "exam.setTracheaDeviationLeft", 1.1, "Л"),
    ...boolOpts("p2-jvd", "jugularVeinsCollapsed", "exam.setJugularVeinsCollapsed", [
      { boolVal: false, label: "Ні", x: 61.45, y: 16.03, labelW: 1.43 },
      { boolVal: true, label: "Так", x: 64.3, y: 16.03, labelW: 2.16 },
    ]),
    // Розширені (distended) Ні/Так
    ...boolOpts("p2-jvd-dist", "jugularVeinsDistended", "exam.setJugularVeinsDistended", [
      { boolVal: false, label: "Ні", x: 56.12, y: 17.12, labelW: 1.43 },
      { boolVal: true, label: "Так", x: 58.97, y: 17.12, labelW: 2.16 },
    ]),
    // Chest symmetric: Симетрична + Несиметрична П/Л
    {
      id: "p2-chest-sym-1",
      label: "Симетрична",
      page: 2,
      x: 58.28,
      y: 18.74,
      w: CW,
      h: CH,
      type: "checkbox",
      labelW: 7.48,
      getValue: (d: any) => String(getRow(d)?.chestSymmetric) === "1",
      command: "exam.setChestSymmetric",
      getPayload: (v: any, d: any, n?: string) => ({
        uuid: n || getUuid(d),
        value: v ? "1" : null,
      }),
      autoCreate: ac,
    },
    ecb("p2-chest-asym-r", 58.58, 19.91, "chestAsymRight", "exam.setChestAsymRight", 1, "П"),
    ecb("p2-chest-asym-l", 61.29, 19.91, "chestAsymLeft", "exam.setChestAsymLeft", 1, "Л"),
    // Chest flail R/L
    ecb(
      "p2-exam-flail-r",
      55.5957,
      21.0217,
      "chestFlailRight",
      "exam.setChestFlailRight",
      1.11,
      "П",
    ),
    ecb(
      "p2-exam-flail-l",
      58.307,
      21.0217,
      "chestFlailLeft",
      "exam.setChestFlailLeft",
      1.1,
      "Л",
    ),
    // Проникна травма Ні/Так
    ...boolOpts("p2-chest-pen-r", "chestPenetratingRight", "exam.setChestPenetratingRight", [
      { boolVal: false, label: "Ні", x: 60.87, y: 22.17, labelW: 1.45 },
      { boolVal: true, label: "Так", x: 63.72, y: 22.17, labelW: 2.22 },
    ]),

    // ── E: Abdomen ──
    ...boolOpts("p2-abd-distended", "abdomenDistended", "exam.setAbdomenDistended", [
      { boolVal: false, label: "Ні", x: 81.81, y: 9.64, labelW: 5.12 },
      { boolVal: true, label: "Так", x: 88.48, y: 9.64, labelW: 4.43 },
    ]),
    ...boolOpts("p2-abd-painful", "abdomenPainful", "exam.setAbdomenPainful", [
      { boolVal: false, label: "Ні", x: 79.54, y: 10.86, labelW: 7.57 },
      { boolVal: true, label: "Так", x: 88.83, y: 10.86, labelW: 5.55 },
    ]),
    ...boolOpts("p2-peritoneal", "peritonealIrritation", "exam.setPeritonealIrritation", [
      { boolVal: false, label: "Ні", x: 88.37, y: 11.99, labelW: 1.43 },
      { boolVal: true, label: "Так", x: 91.22, y: 11.99, labelW: 2.16 },
    ]),
    ...boolOpts("p2-abd-pen", "abdomenPenetratingTrauma", "exam.setAbdomenPenetratingTrauma", [
      { boolVal: false, label: "Ні", x: 83.61, y: 13.06, labelW: 1.43 },
      { boolVal: true, label: "Так", x: 86.63, y: 13.06, labelW: 2.15 },
    ]),
    ...boolOpts("p2-pelvis", "pelvisStable", "exam.setPelvisStable", [
      { boolVal: true, label: "Стаб.", x: 83.1, y: 14.2, labelW: 1.43 },
      { boolVal: false, label: "Нестаб.", x: 86.29, y: 14.2, labelW: 2.15 },
    ]),

    // ── D: Eyes ──
    // ── D: Eyes — Pupils equal ──
    {
      id: "p2-pupils-equal",
      label: "Зіниці",
      page: 2,
      x: 94.05,
      y: 20.74,
      w: 1.4,
      h: 2,
      type: "select",
      getValue: (d: any) => getRow(d)?.pupilsEqual,
      command: "exam.setPupilsEqual",
      getPayload: (v: any, d: any, n?: string) => ({
        uuid: n || getUuid(d),
        value: v,
      }),
      options: [
        { value: "<", label: "<" },
        { value: "=", label: "=" },
        { value: ">", label: ">" },
      ],
      autoCreate: ac,
    },
    // Pupils light reaction — Так S D  Ні S D
    ...boolOpts("p2-pupils-light-l", "pupilsLightReactionLeft", "exam.setPupilsLightReactionLeft", [
      { boolVal: true, label: "Так S", x: 85.73, y: 21.08, labelW: 1 },
      { boolVal: false, label: "Ні S", x: 91.97, y: 21.08, labelW: 1 },
    ]),
    ...boolOpts("p2-pupils-light-r", "pupilsLightReactionRight", "exam.setPupilsLightReactionRight", [
      { boolVal: true, label: "Так D", x: 87.88, y: 21.08, labelW: 1 },
      { boolVal: false, label: "Ні D", x: 94.13, y: 21.08, labelW: 1 },
    ]),
    {
      id: "p2-exam-vision-r",
      label: "Зір П",
      page: 2,
      x: 77.6,
      y: 22.99,
      w: 7.6,
      h: 1.3,
      type: "vision",
      getValue: (d) => getRow(d)?.visionRight,
      command: "exam.setVisionRight",
      getPayload: (v, d, n) => ({ uuid: n || getUuid(d), value: v }),
      autoCreate: ac,
    },
    {
      id: "p2-exam-vision-l",
      label: "Зір Л",
      page: 2,
      x: 88.3,
      y: 22.99,
      w: 7.8,
      h: 1.3,
      type: "vision",
      getValue: (d) => getRow(d)?.visionLeft,
      command: "exam.setVisionLeft",
      getPayload: (v, d, n) => ({ uuid: n || getUuid(d), value: v }),
      autoCreate: ac,
    },
    {
      id: "p2-exam-gcs",
      label: "ШКГ",
      page: 2,
      x: 76.1,
      y: 20.58,
      w: 6.7,
      h: 1.3,
      type: "number",
      getValue: (d) => getRow(d)?.gcsTotal,
      command: "exam.setGcsTotal",
      getPayload: (v, d, n) => ({ uuid: n || getUuid(d), value: v }),
      autoCreate: ac,
    },

    // ── Limb deformation (per-limb JSON) ──
    ...(
      [
        "rue",
        72.9,
        16.48,
        2.7,
        "lue",
        77.3871,
        16.4832,
        2.69,
        "rle",
        81.7346,
        16.4832,
        2.8,
        "lle",
        86.1611,
        16.4832,
        2.8,
      ] as const
    ).reduce<FieldOverlay[]>((arr, _, i, src) => {
      if (i % 4 !== 0) return arr;
      const limb = src[i] as string,
        x = src[i + 1] as number,
        y = src[i + 2] as number,
        lw = src[i + 3] as number;
      const limbLabel =
        { rue: "ПВК", lue: "ЛВК", rle: "ПНК", lle: "ЛНК" }[limb] ?? limb;
      arr.push({
        id: `p2-exam-deform-${limb}`,
        label: limbLabel,
        page: 2,
        x,
        y,
        w: CW,
        h: CH,
        type: "checkbox",
        labelW: lw,
        getValue: (d) => getRow(d)?.limbDeformation?.[limb],
        command: "exam.setLimbDeformationItem",
        getPayload: (v, d, n) => {
          const uuid = n || getUuid(d);
          if (!uuid) return null;
          return { uuid, key: limb, value: !!v };
        },
        autoCreate: ac,
      });
      return arr;
    }, []),
    ...boolOpts("p2-deform-any", "deformationAny", "exam.setDeformationAny", [
      { boolVal: false, label: "Ні", x: 87.46, y: 15.34, labelW: 1.43 },
      { boolVal: true, label: "Так", x: 90.48, y: 15.34, labelW: 2.15 },
    ]),

    // ── Neuro deficit (per-limb JSON) ──
    ...(
      [
        "rue",
        72.9,
        18.76,
        2.7,
        "lue",
        77.3871,
        18.7649,
        2.69,
        "rle",
        81.7346,
        18.7649,
        2.8,
        "lle",
        86.1611,
        18.7649,
        2.8,
      ] as const
    ).reduce<FieldOverlay[]>((arr, _, i, src) => {
      if (i % 4 !== 0) return arr;
      const limb = src[i] as string,
        x = src[i + 1] as number,
        y = src[i + 2] as number,
        lw = src[i + 3] as number;
      const limbLabel =
        { rue: "ПВК", lue: "ЛВК", rle: "ПНК", lle: "ЛНК" }[limb] ?? limb;
      arr.push({
        id: `p2-exam-neuro-${limb}`,
        label: limbLabel,
        page: 2,
        x,
        y,
        w: CW,
        h: CH,
        type: "checkbox",
        labelW: lw,
        getValue: (d) => getRow(d)?.neuroDeficit?.[limb],
        command: "exam.setNeuroDeficitItem",
        getPayload: (v, d, n) => {
          const uuid = n || getUuid(d);
          if (!uuid) return null;
          return { uuid, key: limb, value: !!v };
        },
        autoCreate: ac,
      });
      return arr;
    }, []),
    ...boolOpts("p2-neuro-any", "neuroDeficitAny", "exam.setNeuroDeficitAny", [
      { boolVal: false, label: "Ні", x: 87.46, y: 17.62, labelW: 1.43 },
      { boolVal: true, label: "Так", x: 90.48, y: 17.62, labelW: 2.15 },
    ]),

    // ── Section 16: Додаткова інформація (Additional Info) ──
    {
      id: "p2-exam-additional-info",
      label: "Додаткова інформація",
      page: 2,
      x: 17.9,
      y: 24.57,
      w: 78.26,
      h: 1.2,
      type: "text",
      editAs: "textarea",
      getValue: (d: any) => d.case.r2Care?.examAdditionalInfo,
      command: "case.setR2CareItem",
      getPayload: (v: any, d: any) => ({
        uuid: cid(d),
        key: "examAdditionalInfo",
        value: v,
      }),
    },
  ];
}

// ─── Surgery type checkboxes (Page 2 Section 18.10-18.12) ────────────
// PDF has surgery type checkboxes at y: 39-45%. Uses surgery.setSurgeryTypeItem.

function makeSurgeryTypeCheckboxes(): FieldOverlay[] {
  const ac = {
    command: "surgery.create",
    getPayload: (caseId: string) => ({ caseUuid: caseId, role: 2 }),
  };
  const getRow = (d: any) =>
    (d.surgeries || []).find((r: any) => (r.role ?? 2) === 2) || null;
  const getUuid = (d: any) => getRow(d)?.id;

  //            Row4 y=362.21(43.02%), Row5 y=376.47(44.72%)
  const items: Array<{
    key: string;
    x: number;
    y: number;
    labelW?: number;
    label?: string;
  }> = [
    {
      key: "craniectomy",
      x: 49.76,
      y: 39.45,
      labelW: 7.98,
      label: "Краніектомія",
    },
    {
      key: "cantholysis",
      x: 59.68,
      y: 39.46,
      labelW: 12.85,
      label: "Кантоліз/кантотомія",
    },
    {
      key: "tracheostomy",
      x: 74.6,
      y: 39.46,
      labelW: 8.05,
      label: "Трахеостомія",
    },
    {
      key: "thoracotomy",
      x: 84.87,
      y: 39.46,
      labelW: 7.43,
      label: "Торакотомія",
    },
    {
      key: "pericardiotomy",
      x: 49.76,
      y: 40.65,
      labelW: 10.49,
      label: "Перикардіотомія",
    },
    {
      key: "thoracocentesis",
      x: 61.84,
      y: 40.65,
      labelW: 8.63,
      label: "Торакоцентез",
    },
    {
      key: "laparotomy",
      x: 49.76,
      y: 41.84,
      labelW: 7.73,
      label: "Лапаротомія",
    },
    { key: "azf", x: 59.5, y: 41.84, labelW: 2.36, label: "АЗФ" },
    {
      key: "amputation",
      x: 64.14,
      y: 41.84,
      labelW: 11.63,
      label: "Ампутація кінцівки",
    },
    {
      key: "fasciotomy",
      x: 77.75,
      y: 41.84,
      labelW: 7.46,
      label: "Фасціотомія",
    },
    {
      key: "escharotomy",
      x: 87.22,
      y: 41.84,
      labelW: 7.71,
      label: "Есхаротомія",
    },
    {
      key: "vascularShunt",
      x: 49.76,
      y: 43.02,
      labelW: 9.74,
      label: "Судинний шунт",
    },
    { key: "reboa", x: 49.76, y: 44.72, labelW: 4, label: "REBOA" },
    {
      key: "suturing",
      x: 59.3,
      y: 44.72,
      labelW: 10.3,
      label: "Накладення швів",
    },
    {
      key: "debridement",
      x: 72.41,
      y: 44.72,
      labelW: 17.54,
      label: "Дебридмент",
    },
  ];

  return items.map((item) => ({
    id: `p2-surgery-type-${item.key}`,
    label: item.label,
    page: 2 as const,
    x: item.x,
    y: item.y,
    w: CW,
    h: CH,
    type: "checkbox" as const,
    labelW: item.labelW || 9,
    getValue: (d: any) => {
      const types = getRow(d)?.surgeryTypes;
      return Array.isArray(types) ? types.includes(item.key) : false;
    },
    command: "surgery.setSurgeryTypeItem",
    getPayload: (v: any, d: any, n?: string) => {
      const uuid = n || getUuid(d);
      if (!uuid) return null;
      return { uuid, key: item.key, value: !!v };
    },
    autoCreate: ac,
  }));
}
